import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import * as XLSX from "xlsx";
import "@/App.css";
import axios from "axios";
import {
  Shield,
  ArrowClockwise,
  MagnifyingGlass,
  CheckCircle,
  WarningCircle,
  ClockCounterClockwise,
  ListChecks,
  Gear,
  Trash,
  Plus,
  CircleNotch,
  Circle,
  X,
  CaretRight,
  CaretDown,
  Flask,
  LockSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Toaster, toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { MyStockRequestsTab } from "@/components/MyStockRequestsTab";
import { useAuth } from "@/contexts/AuthContext";
import { formatUnit } from "../lib/formatUnit";
import { QualityNotice } from "@/components/QualityNotice";
import { formatInputQty } from "../lib/formatInputQty";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rowKey = (r) => `${r.production_lot_id}::${r.confirmation_group_uuid}::${r.reporting_point_uuid}`;

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] flex flex-col gap-1.5" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[#475467]">
      <Icon size={14} weight="bold" />
      <span className="font-heading text-xs font-bold uppercase tracking-wider">{label}</span>
    </div>
    <span className="font-sans text-2xl font-bold tabular-nums text-[#1D2939]">{value}</span>
  </div>
);

const formatQty = (v) => (v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: 2 }));

// SAP's own BOM data returns "MASS" as the raw unit label (it's actually
// a dimension/QuantityTypeCode, not a real unit - the Goods Movement bug
// fix this session confirmed the real SAP unit code is "KGM"/kilogram).
// Showing the raw "MASS" text to planners reads as a typo/bug - display
// the real, human-friendly unit instead. Internal values/keys (unit_code
// sent back to the API, etc.) are untouched - this is a display-only map.
// Sep 15 2026, user's explicit ask: show the lot's SAP end date next to Site.
const formatEndDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

// Aug 2026, matches production_confirmation_service.is_usable_stock_status()
// - flags Quality Inspection/Blocked stock in this planner-facing view too
// (same underlying store_requests data as the Store Approval screen).
const RESTRICTED_STOCK_STATUSES = new Set(["inspection", "quality inspection", "blocked", "restricted-use", "restricted", "in transit"]);
const isRestrictedStatus = (status) => RESTRICTED_STOCK_STATUSES.has((status || "").trim().toLowerCase());
// Aug 2026 bug fix: SAP's CRESTRICTED_IND flag is a SEPARATE field from
// stock_status - a row can read stock_status "Not Assigned" (normally
// usable) while still being Restricted Use. See the matching backend
// docstring on is_usable_stock_status for the real incident this was
// traced to.
const isLocationRestricted = (loc) => isRestrictedStatus(loc?.stock_status) || !!loc?.restricted;

// User's explicit ask: today posting/WIP-clearing/by-product outcomes
// only ever show as a toast at confirm-time, then vanish - this renders
// the LAST confirmation's outcome persistently on the row itself.
// Aug 27 2026 (user's explicit ask): a failed "WIP Cleared" chip now
// carries its own inline "Retry" so a stalled step can be fixed right
// here instead of only being visible/actionable elsewhere. Same for a
// failed "FG Moved" chip (SFG -> FG Goods Movement retry).
const LastConfirmationBadges = ({ data, productionLotId, lastConfKey, taskFinished, openQuantity, byproductOutput, siteId, mainOutputProduct, unitCode, actorName, onRetried, isEndReportingPoint = true }) => {
  const [retryingWip, setRetryingWip] = useState(false);
  const [retryingFg, setRetryingFg] = useState(false);
  if (!data) return <span className="text-[#98A2B3] text-xs">—</span>;
  const chip = (ok, label) => (
    <span className={`flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit ${
      ok ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]"
    }`}>
      {ok ? <CheckCircle size={10} weight="fill" /> : <WarningCircle size={10} weight="fill" />}
      {label}
    </span>
  );
  // Aug 2026 fix - see server.py's _all_lot_operations_finished: WIP
  // Clearing (and FG Movement) is skipped, not failed, while other
  // operations on a multi-step lot are still open - a neutral chip so
  // this never looks like a real error with a pointless Retry button.
  const neutralChip = (label, title) => (
    <span title={title} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit bg-[#F9FAFB] text-[#667085] border-[#EAECF0]">
      <Circle size={10} weight="fill" />
      {label}
    </span>
  );
  const retryWip = async () => {
    setRetryingWip(true);
    try {
      const { data: res } = await axios.post(`${API}/production-confirmation/retry-wip-clearing`, {
        production_lot_id: productionLotId, site_id: siteId, actor: actorName.trim(),
      });
      if (res.wip_clearing?.skipped) toast.info(res.wip_clearing.log || "Other operations on this lot are still open - WIP Clearing deferred");
      else if (res.wip_clearing?.success) toast.success(`WIP Clearing Run succeeded for Lot ${productionLotId}`);
      else toast.error(`WIP Clearing Run failed again: ${res.wip_clearing?.log || "see SAP for details"}`);
      onRetried?.(lastConfKey || productionLotId, { wip_clearing: res.wip_clearing });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry WIP Clearing");
    } finally {
      setRetryingWip(false);
    }
  };
  const retryFg = async () => {
    setRetryingFg(true);
    try {
      const { data: res } = await axios.post(`${API}/production-confirmation/retry-fg-movement`, {
        production_lot_id: productionLotId, site_id: siteId, main_output_product: mainOutputProduct,
        confirmed_quantity: data.confirmed_quantity, unit_code: unitCode, actor: actorName.trim(),
      });
      if (res.fg_movement?.skipped) toast.info(res.fg_movement.error_detail || "Other operations on this lot are still open - FG Goods Movement deferred");
      else if (res.fg_movement?.ok) toast.success(`Moved to ${siteId}-FG for Lot ${productionLotId}`);
      else toast.error(`FG Movement failed again: ${res.fg_movement?.error || res.fg_movement?.error_detail || "see SAP for details"}`);
      onRetried?.(lastConfKey || productionLotId, { fg_movement: res.fg_movement });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry FG Movement");
    } finally {
      setRetryingFg(false);
    }
  };
  return (
    <div className="space-y-0.5" data-testid="last-confirmation-badges">
      {chip(!!data.success, "Posted")}
      {data.success && data.confirmation_finished != null && (
        <span className={`flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit ${
          taskFinished ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
        }`} data-testid="last-confirmation-partial-full-badge" title={!taskFinished && openQuantity === 0 ? "All planned quantity is confirmed - the Task itself just wasn't marked Finished, so the next operation stays locked until it is (Confirm again with \"Mark as Finished\" checked, or use Finish Task)." : undefined}>
          {/* Aug 25 2026 fix: was reading the stale historical log's
              confirmation_finished (frozen true even after a restart) -
              now reads the LIVE task status, same field the Confirm
              button itself trusts, so this can never contradict the
              Open Quantity sitting right next to it.
              Sep 27 2026 fix (user's explicit confusion - "i finished
              same qty why its showing partial"): "Partial" used to fire
              purely off `taskFinished`, wrongly implying leftover
              QUANTITY even when Open is already 0 - the real reason was
              the Task was never marked Finished (checkbox left
              unchecked). Now distinguishes the two: only genuinely open
              quantity says "Partial"; a fully-confirmed-but-not-closed
              Task says "Not Finished" instead. */}
          {taskFinished ? <CheckCircle size={10} weight="fill" /> : <Circle size={10} weight="fill" />}
          {taskFinished ? "Full" : openQuantity > 0 ? "Partial" : "Not Finished"}
        </span>
      )}
      {/* Oct 2026, user's explicit rule: a WIP entry only belongs to the END
          task's confirmation, so the WIP chip is shown on the END Reporting
          Point only - on RP_10/RP_20/... it was pure noise. */}
      {data.wip_clearing != null && isEndReportingPoint && (
        <div className="flex items-center gap-1">
          {data.wip_clearing.skipped
            ? neutralChip("WIP Pending", data.wip_clearing.log)
            : chip(!!data.wip_clearing.success, "WIP Cleared")}
          {!data.wip_clearing.skipped && !data.wip_clearing.success && siteId && (
            <button
              type="button"
              disabled={retryingWip}
              onClick={retryWip}
              className="text-[10px] underline text-[#175CD3] hover:text-[#0E4B99] disabled:opacity-50"
              data-testid="retry-wip-clearing-button"
            >
              {retryingWip ? "Retrying..." : "Retry"}
            </button>
          )}
        </div>
      )}
      {data.byproduct_confirmation != null && (
        <div className="flex items-center gap-1">
          {chip(!!data.byproduct_confirmation.success, "By-product")}
          {/* Aug 25 2026, user's explicit ask: don't show a plain green
              tick for by-product when only a fraction of the planned
              scrap has actually been posted - compare live confirmed vs
              planned on the byproduct's OWN MaterialOutput line. */}
          {data.byproduct_confirmation.success && byproductOutput?.planned_quantity != null && (
            <span className={`flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit ${
              (byproductOutput.total_confirmed_quantity || 0) >= byproductOutput.planned_quantity
                ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
            }`} data-testid="byproduct-partial-full-badge">
              {(byproductOutput.total_confirmed_quantity || 0) >= byproductOutput.planned_quantity ? "Full" : "Partial"}
            </span>
          )}
        </div>
      )}
      {data.fg_movement != null && (
        <div className="flex items-center gap-1">
          {data.fg_movement.skipped
            ? neutralChip("FG Pending", data.fg_movement.error_detail)
            : chip(!!data.fg_movement.ok, "FG Moved")}
          {!data.fg_movement.skipped && !data.fg_movement.ok && siteId && mainOutputProduct && (
            <button
              type="button"
              disabled={retryingFg}
              onClick={retryFg}
              className="text-[10px] underline text-[#175CD3] hover:text-[#0E4B99] disabled:opacity-50"
              data-testid="retry-fg-movement-button"
            >
              {retryingFg ? "Retrying..." : "Retry"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Aug 27 2026, user's explicit ask: clear step-by-step progress while
// confirming, instead of one opaque "Posting to SAP..." label for the
// whole duration.
const CONFIRM_PHASE_LABELS = {
  checking_category: "Checking item category (Finished Goods vs Sub-Assembly)...",
  moving_to_fg: "Moving stock to FG warehouse...",
};



const STATUS_TONE = {
  Released: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]",
  Started: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  Finished: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
};

// -------------------- Confirm dialog --------------------
const ConfirmDialog = ({ row, siblingRows, actorName, onClose, onConfirmed, reasons, maxFromPrevStep, prevStepLabel }) => {
  const [confirmedQty, setConfirmedQty] = useState(row ? (row.open_quantity ?? "") : "");
  const [confirmedScrap, setConfirmedScrap] = useState("0");
  const [hasScrap, setHasScrap] = useState(false);
  const [scrapCalc, setScrapCalc] = useState(null);
  const [reason, setReason] = useState("");
  const [finished, setFinished] = useState(true);
  // Oct 7 2026 (live incident, Lot 76643 RP_30): "Mark as Finished" used to
  // default ON, so confirming a PARTIAL quantity set SAP's
  // ConfirmationFinishedIndicator on the Reporting Point - which is
  // PERMANENT and blocks every further confirmation on that RP ("Changes in
  // task or lot not permitted; confirmation complete indicator set"). The
  // remaining 8 of 10 EA became unpostable with no recovery: Restart Task is
  // refused once the task is back "In Process", and SAP offers no way to
  // clear the indicator. 31 Reporting Points across 29 lots were already
  // stuck this way. History proves the safe sequence (lots 69948, 70130):
  // confirm partials with finished=FALSE, then tick it only on the last one.
  // So this now tracks the quantity automatically - ON only when you are
  // confirming the whole remaining amount - until the user overrides it.
  const [finishedTouched, setFinishedTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingElapsed, setSavingElapsed] = useState(0);
  const [savingPhase, setSavingPhase] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  // Oct 27 2026, user's spec: scrap on a model with Quality Activity becomes a
  // Quality Decision Note instead of a SAP posting, and a blocked lot cannot
  // be confirmed at all. Rows for every other model carry no `quality` key, so
  // nothing here shows or changes for them.
  const [sentToQuality, setSentToQuality] = useState(null);
  const [alreadyPosted, setAlreadyPosted] = useState(false);
  // testing_agent iteration_105: abort the poll loop below if this dialog
  // is closed/switched to a different row mid-poll - the background SAP
  // job itself keeps running regardless (nothing is lost), this just
  // stops updating THIS closed instance's state on a stale poll.
  const pollAbortRef = useRef(false);

  // Keep "Finished" in step with the quantity until the user overrides it -
  // ON only when confirming the whole remaining amount. Must sit above the
  // `if (!row) return null` below: hooks cannot run after an early return.
  useEffect(() => {
    if (finishedTouched) return;
    const open = row?.open_quantity;
    const typed = Number(confirmedQty);
    if (open == null || confirmedQty === "" || Number.isNaN(typed)) return;
    setFinished(typed >= open);
  }, [confirmedQty, row?.open_quantity, finishedTouched]);

  // Whether this row is the FIRST operation of its lot's routing. Oct 3 2026:
  // this no longer gates the component stock check - SAP can assign inputs to
  // a LATER operation too (lot 76437's OP_20 POWDER COATING consumes MAT), so
  // the check follows SAP's per-operation input list instead. Still used for
  // the by-product / scrap weight logic, which really is first-operation-only.
  //
  // Sep 30 2026 fix (live incident, Lot 75559 OP_30 POWDER COATING still
  // showing "Net Weight 0.214 kg is greater than Gross Weight 0.005 kg"):
  // the positional test below - "am I the first of my lot's rows currently
  // in the table?" - is only as good as which of that lot's rows happen to
  // be loaded/visible. When a lot's earlier Reporting Points aren't in the
  // list (filters, status selection, the 500-lot page cap), `siblingRows`
  // holds just this one row and a LATER operation wrongly passed as the
  // first. SAP's own routing order is now resolved server-side per lot
  // (`is_first_reporting_point`, see sap_production_lot_client.
  // _parse_lot_block), which cannot be skewed by anything on screen - so
  // trust that whenever it's present and keep the positional test only as
  // a fallback for rows cached before the field existed.
  const isFirstOperation = useMemo(
    () => (
      typeof row?.is_first_reporting_point === "boolean"
        ? row.is_first_reporting_point
        : !siblingRows?.length || !row || rowKey(siblingRows[0]) === rowKey(row)
    ),
    [row, siblingRows]
  );

  useEffect(() => {
    if (!saving) {
      setSavingElapsed(0);
      return;
    }
    const interval = setInterval(() => setSavingElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [saving]);

  useEffect(() => {
    if (row) {
      const qaCleared = row.quality?.status === "approved" ? row.quality.decision : null;
      setConfirmedQty(qaCleared ? String(qaCleared.physical_accepted_quantity ?? "") : (row.open_quantity ?? ""));
      setConfirmedScrap(qaCleared ? String(qaCleared.approved_scrap_quantity ?? 0) : "0");
      setHasScrap(qaCleared ? (qaCleared.approved_scrap_quantity ?? 0) > 0 : false);
      setScrapCalc(null);
      setReason(qaCleared ? (row.quality?.requested?.deviation_reason_code || "") : "");
      // Aug 25 2026: reverted the full-qty-only lock added earlier the
      // same day - user's real workflow deliberately finishes a step
      // early at a partial qty to unlock the next operation (SAP itself
      // requires a task "Finished" before its successor can start), then
      // reopens it later via the Reopen Task button to keep going. What
      // actually prevents the original incident's confusion now is that
      // Open Quantity/task_finished read LIVE state, not a frozen flag.
      setFinished(true);
      setAvailability(null);
      setConfirmError(null);
      setAlreadyPosted(false);
      pollAbortRef.current = false;
      // Sep 25 2026, user's explicit ask/real test finding: by-product
      // is ALWAYS posted at the FIRST operation of a lot's routing (not
      // "only intermediate ops"/"only the last" as earlier sessions
      // assumed) - every LATER operation (2nd...last) must skip the
      // Net/Gross Weight by-product check entirely, since it was
      // already handled at operation 1. `siblingRows` are the same
      // lot's rows in SAP's own routing order (see `awaitingByRowKey`
      // above), so the first entry IS operation 1.
      if (row.main_output_product && isFirstOperation) {
        axios.post(`${API}/production-confirmation/scrap-calc/${encodeURIComponent(row.main_output_product)}`, {
          material_inputs: row.material_inputs || null,
          is_first_reporting_point: isFirstOperation,
        })
          .then(({ data }) => setScrapCalc(data))
          .catch(() => setScrapCalc(null));
      }
    }
    return () => {
      pollAbortRef.current = true;
    };
  }, [row, siblingRows]);

  // NOTE: Confirmed Scrap is a manually-entered REJECTED QUANTITY (defective
  // units the operator is reporting), unrelated to the physical by-product
  // material weight below - it is written to SAP's own ConfirmedScrap field.
  // It must NEVER auto-fill from the weight-based scrap calc.

  useEffect(() => {
    // Oct 3 2026, user's report (lot 76437, OP_20 POWDER COATING): a LATER
    // operation can have its own assigned input - here line item group
    // 20/20 -> MAT "Metallic Black", 0.005 KGM planned - and SAP backflushes
    // it at THAT operation, not at the first one. The old rule skipped the
    // check for anything that wasn't the first operation, so its stock was
    // never checked and its consumption quantity never shown. SAP's own
    // per-operation input list is the right test, not the position in the
    // routing: inputs assigned here => check them; none assigned => check
    // nothing (see no_inputs_assigned_result on the backend).
    if (!row || confirmedQty === "" || Number.isNaN(Number(confirmedQty))) {
      setAvailability(null);
      return;
    }
    const hasOwnInputs = Array.isArray(row.material_inputs) && row.material_inputs.length > 0;
    if (Array.isArray(row.material_inputs) && row.material_inputs.length === 0) {
      setAvailability({
        checked: false,
        no_inputs_assigned: true,
        reason: "No input item is assigned to this operation in SAP - no stock to check.",
        components: [],
      });
      return;
    }
    // No per-operation list at all (a row cached before SAP started sending
    // one): fall back to the old first-operation-only BOM check.
    if (!hasOwnInputs && !isFirstOperation) {
      setAvailability(null);
      return;
    }
    const timer = setTimeout(() => {
      setCheckingAvailability(true);
      axios.post(`${API}/production-confirmation/component-availability`, {
        main_output_product: row.main_output_product, confirmed_quantity: Number(confirmedQty), site_id: row.site_id,
        material_inputs: row.material_inputs || null,
        operation_scoped_inputs: true,
      }).then(({ data }) => setAvailability(data)).catch(() => setAvailability(null)).finally(() => setCheckingAvailability(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [row, confirmedQty, isFirstOperation]);

  if (!row) return null;

  // Match on the LOT's real Output Products grid, not our own AI-guessed
  // scrap-family code - confirmed live that SAP's actual by-product line
  // (e.g. IRON-SCR) doesn't always match our classifier's expected code
  // (e.g. CR-SCRAP guessed from the RM description). Any output line that
  // isn't the main product IS the by-product to confirm.
  const byproductMatch = row.material_outputs?.find((mo) => mo.product_id !== row.main_output_product) || null;

  // If no output line exists AT ALL for the expected by-product, we can
  // still CREATE one from scratch (SAP supports ActionCode="01" on a
  // brand-new MaterialOutput) - reuse the main output's own target
  // logistics area as the destination, since a by-product almost always
  // shares the main output's site/storage area.
  // Sep 11 2026 bug fix, real incident (lot 72647, MAZ42117272-TA): an
  // intermediate Reporting Point (RP_10 "LEFT BLANK+PUNCHING") consumes
  // the raw material (so by-product IS genuinely expected here), but in
  // SAP only the LAST Reporting Point of a multi-op routing ("END") ever
  // carries an Output Products/MaterialOutput line + target storage area
  // - every intermediate RP's OWN material_outputs is legitimately empty
  // by SAP's own design, not a data gap. Falling back to ANY sibling RP
  // of this SAME lot that does have one (in practice always "END") gives
  // a real, valid target storage area instead of hard-blocking every
  // intermediate confirmation with "contact support".
  const mainOutputRow = row.material_outputs?.find((mo) => mo.product_id === row.main_output_product)
    || (siblingRows || []).flatMap((r) => r.material_outputs || []).find((mo) => mo.product_id === row.main_output_product)
    || null;
  const canAutoCreateByproduct = !byproductMatch && !!mainOutputRow?.target_logistics_area_id;

  // Weight-based by-product quantity - fully independent of the manual
  // Confirmed Scrap (rejection) field above.
  const qtyNum = Number(confirmedQty);
  const byproductQty = scrapCalc?.available && confirmedQty !== "" && !Number.isNaN(qtyNum)
    ? Math.round(scrapCalc.scrap_per_unit_kg * qtyNum * 1e6) / 1e6
    : null;

  // Aug 25 2026: reverted - "finished" is a free user toggle again (see
  // note above). Only the qty value itself updates here.
  const updateConfirmedQty = (value) => {
    setConfirmedQty(value);
  };

  // Nov 3 2026, user's report (Lot 82087 / RP-10: "PLAN QTY 3, 1 already
  // confirm, then why system allow 4 qty submit") - identical rule to the
  // live page: every unit booked uses up a planned unit, scrapped ones
  // included, so GOOD + SCRAP must fit in planned - (confirmed + scrapped).
  // Backend enforces the same (server._exceeds_plan).
  const round6 = (n) => Math.round(n * 1e6) / 1e6;
  const alreadyBooked = (row.total_confirmed_quantity || 0) + (row.total_confirmed_scrap || 0);
  const remainingQty = row.planned_quantity ? Math.max(0, round6(row.planned_quantity - alreadyBooked)) : null;
  const requestedQty = round6((Number.isNaN(qtyNum) ? 0 : qtyNum || 0) + (hasScrap ? Number(confirmedScrap) || 0 : 0));
  const exceedsPlan = remainingQty !== null && requestedQty > remainingQty + 1e-6;

  // SAP has already closed this Reporting Point for confirmation and there
  // is no way back (see the finishedTouched note above) - every Confirm from
  // here is a guaranteed rejection, so block it in the UI instead of letting
  // the user discover it from SAP's error. Restart Task is NOT offered here:
  // SAP refuses it with "Action for task N not permitted due to processing
  // status In Process" (verified live on Lot 76643 / task 73307).
  const sapConfirmationClosed =
    row.confirmation_finished === true && (row.open_quantity || 0) > 0 && !row.task_finished;

  // Keep "Finished" in step with the quantity until the user overrides it.

  // Aug 25 2026, user's explicit ask: "if I confirm more at Bending than
  // what actually cleared Blanking, would it go through?" - today
  // nothing on the SAP side stops this (each Reporting Point tracks its
  // own quantity independently, with no material link between them), so
  // this is a real gap without a check here. Cumulative total for THIS
  // step, after this confirmation, cannot exceed what the PRECEDING
  // step has actually confirmed so far.
  const cumulativeAfterThisConfirm = (row.total_confirmed_quantity || 0) + (confirmedQty !== "" && !Number.isNaN(qtyNum) ? qtyNum : 0);
  const exceedsPrevStep = maxFromPrevStep != null && confirmedQty !== "" && !Number.isNaN(qtyNum) && cumulativeAfterThisConfirm > maxFromPrevStep;

  const submit = async () => {
    const qty = confirmedQty === "" ? null : Number(confirmedQty);
    const scrap = confirmedScrap === "" ? null : Number(confirmedScrap);
    if (qty !== null && (Number.isNaN(qty) || qty < 0)) {
      toast.error("Confirmed Output Quantity must be a valid, non-negative number");
      return;
    }
    if (scrap !== null && (Number.isNaN(scrap) || scrap < 0)) {
      toast.error("Confirmed Scrap must be a valid, non-negative number");
      return;
    }
    if (exceedsPlan) {
      toast.error(`Only ${remainingQty} ${formatUnit(row.unit_code)} can still be booked on this step`, {
        description: `Planned ${row.planned_quantity}, already ${row.total_confirmed_quantity || 0} confirmed`
          + `${row.total_confirmed_scrap ? ` and ${row.total_confirmed_scrap} scrapped` : ""}`
          + `. You entered ${requestedQty}${hasScrap ? ` (${qtyNum || 0} good + ${Number(confirmedScrap) || 0} scrap)` : ""}.`,
      });
      return;
    }
    if (exceedsPrevStep) {
      toast.error(`Cannot confirm ${cumulativeAfterThisConfirm} at this step - only ${maxFromPrevStep} have actually cleared ${prevStepLabel || "the previous step"} so far. Reduce the quantity or post more at ${prevStepLabel || "the previous step"} first.`);
      return;
    }
    // Aug 25 2026, user's explicit ask: scrap is meaningful data (QC
    // rejects, damage, etc.) - it must never be entered "by accident" as
    // a stray number, and it must always carry a reason so the shop
    // floor can trace WHY units were rejected. Gated behind the
    // "Has Scrap" checkbox itself, so this only fires when that's on.
    if (hasScrap) {
      if (scrap === null || scrap <= 0) {
        toast.error("Scrap Quantity must be greater than 0 when \"This confirmation has Scrap\" is checked");
        return;
      }
      if (!reason) {
        toast.error("Select a Scrap Reason (e.g. Quality Issue) before confirming");
        return;
      }
    }
    // Aug 27 2026 fix (user's explicit ask - a real confirmation went
    // through with no by-product ever posted): block submission
    // up-front whenever a by-product IS expected (an RM component was
    // found) but can't actually be posted right now - either the weight
    // data is missing, or there's nowhere to post it to - instead of
    // silently letting the confirmation through with by-product fields
    // all null. Skipped only when scrapCalc genuinely found no RM
    // component at all (a real assembly-only item with no by-product).
    if (scrapCalc?.rm_product_id && !scrapCalc.available) {
      toast.error(`Cannot confirm: by-product is expected but its weight can't be calculated yet (${scrapCalc.reason}). Fix this first so the by-product is never skipped.`);
      return;
    }
    // Oct 29 2026 (Lot 81217): only a by-product with a REAL quantity needs
    // somewhere to go. A cut-to-length part whose net weight equals its gross
    // weight produces 0 kg, and blocking that deadlocked the confirmation.
    if (scrapCalc?.available && byproductQty > 0 && !byproductMatch && !canAutoCreateByproduct) {
      toast.error("Cannot confirm: a by-product is expected but there's no Output Products line to post it to and no target storage area to create one - contact support before confirming this lot.");
      return;
    }
    setSaving(true);
    setSavingPhase(null);
    setConfirmError(null);
    try {
      const { data: jobData } = await axios.post(`${API}/production-confirmation/confirm`, {
        production_lot_id: row.production_lot_id,
        production_lot_uuid: row.production_lot_uuid,
        confirmation_group_uuid: row.confirmation_group_uuid,
        reporting_point_uuid: row.reporting_point_uuid,
        reporting_point_id: row.reporting_point_id,
        main_output_product: row.main_output_product,
        unit_code: row.unit_code,
        production_task_id: row.production_task_id,
        production_task_uuid: row.production_task_uuid,
        confirmed_quantity: qty,
        confirmed_scrap: scrap,
        deviation_reason_code: reason || null,
        confirmation_finished: finished,
        site_id: row.site_id,
        byproduct_material_output_uuid: byproductMatch && byproductQty > 0 ? byproductMatch.material_output_uuid : null,
        byproduct_confirmed_quantity: byproductMatch && byproductQty > 0 ? byproductQty : null,
        byproduct_unit_code: byproductMatch && byproductQty > 0 ? byproductMatch.unit_code : null,
        new_byproduct_product_id: canAutoCreateByproduct && byproductQty > 0 ? scrapCalc.scrap_family.expected_byproduct_code : null,
        new_byproduct_target_logistics_area_id: canAutoCreateByproduct && byproductQty > 0 ? mainOutputRow.target_logistics_area_id : null,
        new_byproduct_confirmed_quantity: canAutoCreateByproduct && byproductQty > 0 ? byproductQty : null,
        new_byproduct_unit_code: canAutoCreateByproduct && byproductQty > 0 ? "KGM" : null,
        // Sep 27 2026 fix - real bug found: this Test page's confirm
        // call was missing these 2 fields (the main
        // ProductionConfirmationPage.js already sends them) - without
        // production_model_id, the backend's check_confirm_authorization
        // can't resolve the BOO/Department for this row at all and
        // safe-defaults to ALLOWED, so a Department restriction never
        // actually applied on THIS page even though the row itself was
        // already correctly locked/unlocked in the table.
        production_model_id: row.production_model_id || null,
        reporting_point_description: row.reporting_point_description || row.operation_description || null,
        material_inputs: row.material_inputs || null,
        // Backend mirrors the same first-operation-only by-product rule
        // (see _compute_scrap_calc) - without this flag its own
        // enforcement would still block a later operation, even though
        // this screen correctly skipped the check for it.
        is_first_reporting_point: isFirstOperation,
        actor: actorName.trim(),
      });
      // Runs as a background job (Aug 2026 fix) - posting to SAP can take
      // long enough (by-product + main + finish task + WIP clearing, up
      // to 4 sequential SOAP calls) to blow past the platform's ingress
      // timeout and surface as a raw, unhelpful Cloudflare error instead
      // of a real SAP message (reproduced live on Lot 70222 with short
      // stock). Polling here instead means that can never happen again.
      // testing_agent iteration_105: bounded to a 3-min deadline (well
      // above the realistic worst case for up to 4 sequential SOAP
      // calls) and retries a single transient status-GET failure instead
      // of immediately reporting a false failure - the background job
      // keeps running server-side either way, nothing is lost.
      if (jobData.quality_decision) {
        setSentToQuality(jobData.quality_decision);
        toast.success(`Scrap sent to quality as ${jobData.quality_decision.note_id}`, {
          description: `Lot ${row.production_lot_id} stays blocked until quality accepts the scrap - nothing posted to SAP.`,
        });
        return;
      }
      const deadline = Date.now() + 3 * 60 * 1000;
      let job = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        if (pollAbortRef.current) return;
        try {
          const { data } = await axios.get(`${API}/production-confirmation/confirm/status/${jobData.job_id}`);
          job = data;
          // Aug 27 2026 (user's explicit ask): "show progress with steps
          // clearly" while the backend is live-checking category/moving
          // stock to FG, instead of one opaque "Posting to SAP..." label.
          setSavingPhase(job.status);
        } catch {
          continue; // transient network hiccup - just retry on the next tick
        }
        if (job.status === "done" || job.status === "failed") break;
        job = null;
      }
      if (pollAbortRef.current) return;
      if (!job) {
        setConfirmError(`Still waiting on SAP for Lot ${row.production_lot_id} after 3 minutes - it may still complete in the background. Check History shortly before retrying to avoid a duplicate confirmation.`);
        return;
      }
      if (job.status === "failed") {
        // Persistent on-screen banner instead of a toast (Aug 2026 user
        // feedback: "the error is a toast, instead show a message on
        // screen user will close so it does not disappear") - a real SAP
        // rejection/timeout message here is important enough that it
        // must stay visible until the user dismisses it themselves.
        setConfirmError(job.error || "Failed to post confirmation to SAP");
        return;
      }
      const data = job.result;
      if (data.success) {
        toast.success(`Confirmation posted to SAP for Lot ${row.production_lot_id}`);
        if (data.byproduct_confirmation) {
          if (data.byproduct_confirmation.success) {
            toast.success(`By-product ${byproductMatch?.product_id || "quantity"} (${byproductQty ?? 0} ${formatUnit(byproductMatch?.unit_code)}) posted to SAP`);
          } else {
            toast.error(`By-product quantity failed to post: ${data.byproduct_confirmation.logs?.map((l) => l.note).join("; ") || "see history for details"}`);
          }
        }
        if (finished && data.wip_clearing) {
          if (data.wip_clearing.skipped) {
            toast.info(data.wip_clearing.log || `WIP Clearing deferred for Lot ${row.production_lot_id} - other operations are still open`);
          } else if (data.wip_clearing.success) {
            toast.success(`WIP Clearing Run triggered for Lot ${row.production_lot_id}`);
          } else {
            toast.error(`WIP Clearing Run failed: ${data.wip_clearing.log || "see history for details"}`);
          }
        }
        if (data.fg_movement) {
          if (data.fg_movement.skipped) {
            toast.info(data.fg_movement.error_detail || `FG Goods Movement deferred for Lot ${row.production_lot_id} - other operations are still open`);
          } else if (data.fg_movement.ok) {
            toast.success(`Finished Goods moved to ${row.site_id}-FG for Lot ${row.production_lot_id}`);
          } else {
            toast.error(`FG Movement failed: ${data.fg_movement.error || data.fg_movement.error_detail || "see history for details"}`);
          }
        }
        onConfirmed(row);
      } else {
        // clarified_error (Aug 2026, testing_agent iteration_105): a
        // normal SAP business rejection (no exception) now gets the same
        // readable guidance as an exception/timeout path instead of raw
        // SAP log text like "Modification failed".
        setConfirmError(data.clarified_error || `SAP reported an issue: ${data.logs?.map((l) => l.note).join("; ") || "see history for details"}`);
      }
    } catch (e) {
      if (!pollAbortRef.current) {
        // 409 = the backend re-read SAP and this reporting point is already
        // closed; the quantity IS in SAP, so this is not a failure.
        if (e.response?.status === 409) setAlreadyPosted(true);
        setConfirmError(e.response?.data?.detail || "Failed to post confirmation to SAP");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="confirm-production-dialog">
        <DialogHeader>
          <DialogTitle>Confirm Production Task</DialogTitle>
          <DialogDescription>
            Lot {row.production_lot_id} · {row.main_output_product || "—"} · Reporting Point {row.reporting_point_description || row.reporting_point_id || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <QualityNotice quality={row.quality} sentToQuality={sentToQuality} unit={formatUnit(row.unit_code)} />
          {alreadyPosted ? (
            <Alert className="rounded-sm border-[#FEDF89] bg-[#FFFAEB]" data-testid="already-confirmed-banner">
              <WarningCircle size={16} />
              <AlertTitle className="font-heading text-sm">Already confirmed in SAP</AlertTitle>
              <AlertDescription className="font-sans text-[13px] break-words">{confirmError}</AlertDescription>
            </Alert>
          ) : confirmError && (
            <Alert variant="destructive" className="relative pr-9 rounded-sm border-[#F04438]/40 bg-[#FEF3F2]" data-testid="confirm-error-banner">
              <WarningCircle size={16} />
              <AlertTitle className="font-heading text-sm">Confirmation failed</AlertTitle>
              <AlertDescription className="font-sans text-[13px] break-words">{confirmError}</AlertDescription>
              <button
                type="button"
                onClick={() => setConfirmError(null)}
                className="absolute top-3 right-3 text-[#B42318]/70 hover:text-[#B42318]"
                data-testid="confirm-error-banner-close"
                aria-label="Dismiss error"
              >
                <X size={16} />
              </button>
            </Alert>
          )}
          {sapConfirmationClosed && (
            <Alert variant="destructive" className="rounded-sm border-[#F04438]/40 bg-[#FEF3F2]" data-testid="sap-confirmation-closed-alert">
              <WarningCircle size={16} />
              <AlertTitle className="font-heading text-sm">SAP has closed this Reporting Point</AlertTitle>
              <AlertDescription className="font-sans text-[13px] break-words">
                An earlier confirmation on {row.reporting_point_description || row.reporting_point_id} was posted with
                "Finished" ticked, so SAP set its Confirmation Finished indicator. That indicator is permanent - SAP
                rejects any further confirmation here with "confirmation complete indicator set", and it refuses to
                reopen the task because its status is already back to "In Process".
                {" "}{formatQty(row.open_quantity)} {formatUnit(row.unit_code)} cannot be posted against this
                Reporting Point any more. This needs a correction in SAP itself - reduce the lot's planned quantity to
                what was actually confirmed and raise a new lot for the balance, or ask the SAP team to reset the
                indicator.
              </AlertDescription>
            </Alert>
          )}
          <div className="text-xs text-[#667085] bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-3 py-2">
            Planned: <strong className="text-[#1D2939]">{formatQty(row.planned_quantity)}</strong> {formatUnit(row.unit_code)} ·
            {" "}Open: <strong className="text-[#1D2939]">{formatQty(row.open_quantity)}</strong> {formatUnit(row.unit_code)}
            {row.total_confirmed_scrap > 0 && (
              <> · Already scrapped: <strong className="text-[#B54708]">{formatQty(row.total_confirmed_scrap)}</strong> {formatUnit(row.unit_code)}
                {" "}· Can still book: <strong className="text-[#1D2939]">{formatQty(remainingQty)}</strong> {formatUnit(row.unit_code)}</>
            )}
            {maxFromPrevStep != null && (
              <> · Available from {prevStepLabel || "previous step"}: <strong className="text-[#1D2939]">{formatQty(maxFromPrevStep)}</strong> {formatUnit(row.unit_code)}</>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Confirmed Output Quantity</Label>
            <Input type="number" value={confirmedQty} onChange={(e) => updateConfirmedQty(e.target.value)} data-testid="confirm-qty-input" />
            {exceedsPlan && (
              <p className="text-xs text-[#B42318] font-bold mt-1" data-testid="confirm-qty-exceeds-plan-warning">
                {requestedQty} {formatUnit(row.unit_code)}{hasScrap ? ` (${qtyNum || 0} good + ${Number(confirmedScrap) || 0} scrap)` : ""} is
                more than this step can still take - only {remainingQty} {formatUnit(row.unit_code)} of the planned
                {" "}{formatQty(row.planned_quantity)} is left. Reduce it, or raise the planned quantity on the lot in SAP first.
              </p>
            )}
            {exceedsPrevStep && (
              <p className="text-xs text-[#B42318] mt-1 font-bold" data-testid="confirm-qty-exceeds-prev-step-error">
                Blocked: only {maxFromPrevStep} {formatUnit(row.unit_code)} have cleared {prevStepLabel || "the previous step"} so far - you cannot confirm {cumulativeAfterThisConfirm} here yet.
              </p>
            )}
          </div>

          {checkingAvailability && <p className="text-xs text-[#98A2B3]">Checking component stock...</p>}
          {availability?.checked && (
            <div className="border border-[#EAECF0] rounded-sm overflow-hidden" data-testid="component-availability-panel">
              <div className="bg-[#F9FAFB] px-3 py-1.5 text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">
                Component Stock Check {row.site_id ? `(Site ${row.site_id})` : ""}
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-[#EAECF0]">
                {availability.components.map((c) => (
                  <div key={c.product_id} className="px-3 py-1.5 text-xs" data-testid={`component-row-${c.product_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[#344054] truncate mr-2">{c.product_id}{c.description ? ` - ${c.description}` : ""}</span>
                      <span className={`shrink-0 tabular-nums ${c.sufficient ? "text-[#027A48]" : c.available_qty === null ? "text-[#98A2B3]" : "text-[#B42318] font-bold"}`}>
                        {c.available_qty === null ? "no stock data" : `${formatQty(c.available_qty)} / ${formatInputQty(c.required_qty)} ${formatUnit(c.unit_of_measure)}`}
                        {c.sufficient ? " ✓" : c.available_qty !== null ? " ✗" : ""}
                      </span>
                    </div>
                    {/* Per-warehouse/stock-status breakdown at this site - user's
                        explicit ask (Aug 2026): show SITE + Warehouse (e.g. RM/
                        SFG/FG) + Stock Status, not just a single aggregate qty. */}
                    {c.locations && c.locations.length > 0 && (
                      <div className="mt-0.5 pl-2 text-[11px] text-[#667085] space-y-0.5" data-testid={`component-locations-${c.product_id}`}>
                        {c.locations.map((loc, li) => (
                          <div key={li}>
                            {row.site_id}{loc.warehouse ? ` / ${loc.warehouse}` : ""}{loc.stock_status ? ` (${loc.stock_status})` : ""}: {formatQty(loc.qty)} {formatUnit(c.unit_of_measure)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {availability.components.length === 0 && <div className="px-3 py-1.5 text-xs text-[#98A2B3]">No active components in cached BOM.</div>}
              </div>
              {availability.components.some((c) => !c.sufficient && c.available_qty !== null) && (
                <div className="bg-[#FFFAEB] px-3 py-1.5 text-[11px] text-[#B54708]">Some components may be short - SAP's backflush could reject this confirmation.</div>
              )}
            </div>
          )}
          {availability && !availability.checked && (
            <p className="text-[11px] text-[#98A2B3]" data-testid="component-availability-not-checked">{availability.reason}</p>
          )}

          {scrapCalc?.available && (
            <div className="text-xs text-[#344054] bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-3 py-2" data-testid="scrap-calc-info">
              <div className="font-bold font-heading uppercase tracking-wide text-[10px] text-[#667085] mb-1">Auto-calculated from {scrapCalc.rm_product_id}{scrapCalc.rm_description ? ` (${scrapCalc.rm_description})` : ""}</div>
              Gross Weight: <strong data-testid="scrap-calc-gross">{scrapCalc.gross_weight_kg}</strong> kg ·
              {" "}Net Weight: <strong data-testid="scrap-calc-net">{scrapCalc.net_weight_kg}</strong> kg ·
              {" "}Scrap/unit: <strong data-testid="scrap-calc-per-unit">{scrapCalc.scrap_per_unit_kg}</strong> kg
              {scrapCalc.scrap_family && (
                <div className="mt-1 text-[#667085]" data-testid="scrap-calc-family">
                  Expected by-product: {scrapCalc.scrap_family.family} ({scrapCalc.scrap_family.expected_byproduct_code})
                  {byproductMatch
                    ? <span className="text-[#027A48]" data-testid="byproduct-match-found"> · will post {byproductQty ?? 0} {formatUnit(byproductMatch.unit_code)} of {byproductMatch.product_id} to SAP</span>
                    : canAutoCreateByproduct
                      ? <span className="text-[#0E7C86]" data-testid="byproduct-auto-create"> · no output line exists yet - will auto-create it and post {byproductQty ?? 0} KGM to SAP</span>
                      : <span className="text-[#B54708]" data-testid="byproduct-match-missing"> · no by-product output line found on this lot - only Confirmed Quantity will be posted</span>}
                </div>
              )}
            </div>
          )}
          {scrapCalc && !scrapCalc.available && scrapCalc.rm_product_id && (
            <div className="text-[11px] text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-sm px-3 py-2 flex items-start gap-1.5" data-testid="scrap-calc-unavailable">
              <WarningCircle size={13} weight="fill" className="shrink-0 mt-0.5" />
              <span><strong>Cannot confirm yet:</strong> {scrapCalc.reason}</span>
            </div>
          )}
          <div className="border border-[#D0D5DD] rounded-sm p-3 space-y-3" data-testid="scrap-section">
            <div className="flex items-center gap-2">
              <Checkbox
                id="has-scrap-cb"
                checked={hasScrap}
                onCheckedChange={(checked) => {
                  setHasScrap(!!checked);
                  if (!checked) { setConfirmedScrap("0"); setReason(""); }
                }}
                data-testid="confirm-has-scrap-checkbox"
              />
              <Label htmlFor="has-scrap-cb" className="text-xs font-bold text-[#344054]">This confirmation has Scrap / Rejected units (QC issue, damage, etc.)</Label>
            </div>
            {hasScrap && (
              <>
                <div>
                  <Label className="text-xs font-bold text-[#344054]">Scrap Quantity (Rejected Qty) *</Label>
                  <Input type="number" value={confirmedScrap} onChange={(e) => setConfirmedScrap(e.target.value)} data-testid="confirm-scrap-input" />
                  <p className="text-[11px] text-[#98A2B3] mt-0.5">Manually enter rejected/defective units. Unrelated to the by-product weight above.</p>
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#344054]">Scrap Reason *</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="confirm-deviation-reason-select"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
                    <SelectContent>
                      {reasons.map((r) => (
                        <SelectItem key={r.code} value={r.code}>{r.code} - {r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#98A2B3] mt-0.5">Required whenever scrap units are recorded (e.g. Quality Issue, Material Damage).</p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="finished-cb"
              checked={finished}
              onCheckedChange={(c) => { setFinishedTouched(true); setFinished(!!c); }}
              data-testid="confirm-finished-checkbox"
            />
            <Label htmlFor="finished-cb" className="text-sm text-[#344054]">Mark this task as Finished (unlocks the next operation - leave it OFF for a partial, SAP can never reopen a Reporting Point once this is set)</Label>
          </div>
          {finished && confirmedQty !== "" && !Number.isNaN(qtyNum) && row.open_quantity != null && qtyNum < row.open_quantity && (
            <p className="text-xs text-[#B42318] font-bold" data-testid="early-finish-warning">
              Warning: this confirms {confirmedQty} of {row.open_quantity} still open AND marks the task Finished. SAP
              will permanently close this Reporting Point - the remaining {formatQty(row.open_quantity - qtyNum)}{" "}
              {formatUnit(row.unit_code)} can never be confirmed here afterwards (it cannot be reopened). Untick
              "Finished" unless this lot really is complete at {confirmedQty}.
            </p>
          )}
          <p className="text-[11px] text-[#98A2B3]">Component/input quantities are NOT sent - SAP's backflush auto-consumes BOM inputs from the confirmed output.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="confirm-cancel-button">Cancel</Button>
          {alreadyPosted ? (
            <Button onClick={() => onConfirmed(row)} data-testid="already-confirmed-close-button">Close &amp; refresh list</Button>
          ) : (
          <Button onClick={submit} disabled={row.quality?.blocked || !!sentToQuality || saving || checkingAvailability || exceedsPlan || exceedsPrevStep || sapConfirmationClosed || (scrapCalc?.rm_product_id && !scrapCalc?.available)} data-testid="confirm-submit-button">
            {saving ? `${CONFIRM_PHASE_LABELS[savingPhase] || "Posting to SAP"} (${savingElapsed}s)...` : checkingAvailability ? "Checking stock..." : sapConfirmationClosed ? "Closed in SAP" : (scrapCalc?.rm_product_id && !scrapCalc?.available) ? "Fix weight data first" : "Post Confirmation"}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Manage Reasons dialog --------------------
const ManageReasonsDialog = ({ open, onClose, reasons, onChanged }) => {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");

  const add = async () => {
    if (!code.trim() || !label.trim()) return;
    const isUpdate = reasons.some((r) => r.code === code.trim());
    try {
      const { data } = await axios.post(`${API}/production-confirmation/deviation-reasons`, { code: code.trim(), label: label.trim() });
      onChanged(data.reasons);
      setCode("");
      setLabel("");
      toast.success(isUpdate ? `Updated reason ${code.trim()}` : `Added reason ${code.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save deviation reason");
    }
  };

  const remove = async (c) => {
    try {
      const { data } = await axios.delete(`${API}/production-confirmation/deviation-reasons/${c}`);
      onChanged(data.reasons);
      toast.success(`Removed reason ${c}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete deviation reason");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="manage-reasons-dialog">
        <DialogHeader>
          <DialogTitle>Manage Deviation Reasons</DialogTitle>
          <DialogDescription>Seeded with SAP's standard code list - adjust to match this tenant's exact fine-tuning if different.</DialogDescription>
        </DialogHeader>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {reasons.map((r) => (
            <div key={r.code} className="flex items-center justify-between px-2 py-1.5 rounded-sm bg-[#F9FAFB] border border-[#EAECF0]" data-testid={`reason-row-${r.code}`}>
              <span className="text-sm text-[#344054]"><strong>{r.code}</strong> - {r.label}</span>
              <button onClick={() => remove(r.code)} className="text-[#B42318] hover:text-[#912018]" data-testid={`reason-delete-${r.code}`}>
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-[#EAECF0]">
          <Input placeholder="Code (e.g. 009)" value={code} onChange={(e) => setCode(e.target.value)} className="w-28" data-testid="reason-code-input" />
          <Input placeholder="Label" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="reason-label-input" />
          <Button onClick={add} data-testid="reason-add-button"><Plus size={14} /></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- History dialog --------------------
const HistoryDialog = ({ open, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/production-confirmation/history`).then(({ data }) => setEntries(data.entries)).finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="confirmation-history-dialog">
        <DialogHeader>
          <DialogTitle>Confirmation History</DialogTitle>
          <DialogDescription>Every confirmation submitted from this page, most recent first.</DialogDescription>
        </DialogHeader>
        {loading ? <Skeleton className="h-40 w-full" /> : (
          <div className="border border-[#D0D5DD] rounded-sm overflow-auto">
            <table className="w-full text-[12px] border-collapse" data-testid="confirmation-history-table">
              <thead>
                <tr>
                  {["When", "By", "Lot", "Product", "Qty", "Scrap", "Finished", "Result", "WIP Clearing", "FG Movement"].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`history-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1">{new Date(e.at).toLocaleString("en-IN")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1">{e.actor}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1">{e.production_lot_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1">{e.main_output_product || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1 text-right tabular-nums">{formatQty(e.confirmed_quantity)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1 text-right tabular-nums">{formatQty(e.confirmed_scrap)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1">{e.confirmation_finished ? "Yes" : "No"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1">
                      {e.success ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Success</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Failed</Badge>}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1">
                      {!e.wip_clearing ? "—" : e.wip_clearing.skipped ? <Badge variant="outline" className="bg-[#F9FAFB] text-[#667085] border-[#EAECF0]" title={e.wip_clearing.log}>Pending</Badge> : e.wip_clearing.success ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Cleared</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Failed</Badge>}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1">
                      {!e.fg_movement ? "—" : e.fg_movement.ok ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Moved</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Failed</Badge>}
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]">No confirmations submitted yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Create Production Order tab --------------------
const ACTIVE_JOBS_STORAGE_KEY = "productionConfirmationActiveJobs";
const POLL_INTERVAL_MS = 4000;

// These 2 statuses PAUSE the automated pipeline for a human action (store
// issuing stock, or the requester deciding on a partial issue).
const PAUSED_STATUSES = ["waiting_store_approval", "partial_pending_planner"];

const PHASE_LABELS = {
  running: "Submitting...",
  checking_stock: "Checking Stock",
  creating_proposal: "Creating Proposal",
  waiting_for_order: "Posting to SAP",
  releasing_order: "Releasing Order",
  waiting_store_approval: "Waiting for Store",
  partial_pending_planner: "Awaiting Your Decision",
  paused_sap_block: "Paused - SAP Rejected",
};

// Linear happy-path order of the automated pipeline (excludes the 2
// PAUSED_STATUSES above, which branch off to their own Badge+actions UI,
// and excludes "done"/"failed"/"cancelled" which remove the row entirely
// via a toast the instant they're seen - see pollJob). Some of these
// steps (creating_proposal in particular, often also releasing_order when
// SAP self-releases) can complete in well under one poll interval, so a
// user watching only the CURRENT status would rarely if ever see them -
// this tracker instead marks every step up to the current one as done
// (checkmarked) so a fast step still visibly registers as completed
// rather than seeming to have never happened.
const ORDER_STEP_KEYS = ["checking_stock", "creating_proposal", "waiting_for_order", "releasing_order"];

const OrderStepTracker = ({ status, elapsedSeconds }) => {
  // Any status not yet in ORDER_STEP_KEYS (e.g. "running", seeded right
  // after submit or restored from localStorage before the first poll
  // resolves) is treated as step 0 "current" rather than indexOf's -1
  // (which would render every step gray/pending with no spinner at all -
  // testing_agent iteration_101 caught this as a ~4s cosmetic gap).
  const rawIndex = ORDER_STEP_KEYS.indexOf(status);
  const currentIndex = rawIndex === -1 ? 0 : rawIndex;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="order-step-tracker">
      {ORDER_STEP_KEYS.map((key, idx) => {
        const isDone = currentIndex > idx;
        const isCurrent = currentIndex === idx;
        return (
          <span key={key} className="flex items-center gap-1">
            <span
              className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${
                isDone ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]"
                : isCurrent ? "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]"
                : "bg-[#F9FAFB] text-[#98A2B3] border-[#EAECF0]"
              }`}
              data-testid={`order-step-${key}`}
              data-step-state={isDone ? "done" : isCurrent ? "current" : "pending"}
            >
              {isDone ? <CheckCircle size={11} weight="fill" /> : isCurrent ? <CircleNotch size={11} className="animate-spin" /> : <Circle size={11} />}
              {PHASE_LABELS[key]}
            </span>
            {idx < ORDER_STEP_KEYS.length - 1 && <span className="text-[#D0D5DD]">{"\u2192"}</span>}
          </span>
        );
      })}
      <span className="text-[11px] text-[#98A2B3] tabular-nums ml-1" data-testid="order-step-elapsed">{elapsedSeconds}s</span>
    </div>
  );
};

// Same note as the main Production Confirmation page (Oct 30 2026, user's ask):
// the SAP order number the instant it is readable, and on a release that has not
// gone through, the next automatic attempt plus the manual route.
const OrderReleaseNote = ({ job, index, onUseOrderId }) => {
  const secondsLeft = job.nextReleaseAttemptAt
    ? Math.max(0, Math.round((new Date(job.nextReleaseAttemptAt).getTime() - Date.now()) / 1000))
    : null;
  if (job.orderReleaseState !== "retrying") {
    return (
      <div className="text-[11px] text-[#175CD3] mt-1" data-testid={`active-order-release-detail-${index}`}>
        Order <strong>{job.productionOrderId}</strong> is created in SAP - processing its release now
      </div>
    );
  }
  return (
    <div className="text-[11px] text-[#B54708] mt-1 space-y-0.5" data-testid={`active-order-release-detail-${index}`}>
      <div>
        Order <strong>{job.productionOrderId}</strong> is created in SAP but still In Preparation
        {job.orderReleaseError ? ` - SAP said: ${job.orderReleaseError}` : ""}
      </div>
      <div>
        Attempt #{job.orderReleaseAttempts || 1} done. Next automatic release{" "}
        {secondsLeft === null ? "shortly" : secondsLeft > 0 ? `in ${secondsLeft}s` : "now"} - either wait for it, or
        release order {job.productionOrderId} yourself in SAP
        {onUseOrderId && (
          <>
            {" or "}
            <button
              type="button"
              onClick={() => onUseOrderId(job.productionOrderId)}
              className="underline font-bold"
              data-testid={`active-order-release-manual-${index}`}
            >
              from the Release Production Order box
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CreateOrderTab = ({ actorName }) => {
  const [materialId, setMaterialId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitCode, setUnitCode] = useState("EA");
  const [requestedEndDate, setRequestedEndDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Oct 22 2026, user's explicit ask: the output item's Valuation must be
  // Active on Moving Average at that site before a proposal is created. The
  // reason stays on screen - a toast is gone before anyone can read a
  // master-data instruction.
  const [valuationBlock, setValuationBlock] = useState(null);
  const [releaseOrderId, setReleaseOrderId] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sosOptions, setSosOptions] = useState([]);
  const [sosLoading, setSosLoading] = useState(false);
  const [sosChecked, setSosChecked] = useState(false);
  const [selectedSosKey, setSelectedSosKey] = useState("");
  const [siteAutoFilled, setSiteAutoFilled] = useState(false);
  const [unitCodeAutoFilled, setUnitCodeAutoFilled] = useState(false);
  const [materialUuid, setMaterialUuid] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  // Holistic (every site/warehouse, not just this order's site) BOM
  // Component Stock Status panel (Aug 2026, user's explicit ask) - shown
  // on-demand via the "Check Stock" button next to Create Production
  // Order (not automatic), splitting this card's space in half with the
  // form once open. Required/Shortfall are computed client-side from
  // `bom_qty_per_unit` x whatever Quantity is currently typed, so
  // adjusting Quantity updates the shortage live with no extra API call.
  const [showBomPanel, setShowBomPanel] = useState(false);
  const [bomStockStatus, setBomStockStatus] = useState(null);
  const [bomStockLoading, setBomStockLoading] = useState(false);
  // "Refresh Live SFG Stock" (Aug 2026, user's explicit ask): once a short
  // sub-assembly's own production order is confirmed, pull its updated
  // SFG warehouse stock right away instead of waiting for the scheduled
  // inventory_cache refresh, then retry order creation.
  const [refreshingSfgStock, setRefreshingSfgStock] = useState(false);
  const [refreshSfgElapsed, setRefreshSfgElapsed] = useState(0);
  const [refreshSfgStatus, setRefreshSfgStatus] = useState(null);
  // Every in-flight create-and-release job this browser session knows
  // about, tracked in the "Active Orders" table below - NOT tied to the
  // form, so submitting one order never blocks starting another while the
  // first is still running/paused in the background.
  // Lazily hydrated straight from localStorage during the initial render
  // (not via a post-mount effect) - fixes a real bug where React 18
  // StrictMode's dev-only double-invoke of effects on mount raced the old
  // "restore, then persist" effect pair and clobbered localStorage with
  // "[]" before the restored job ever reached state, silently wiping an
  // in-flight order's tracking (and its Stop button) on every refresh.
  const [activeJobs, setActiveJobs] = useState(() => {
    let stored = [];
    try { stored = JSON.parse(localStorage.getItem(ACTIVE_JOBS_STORAGE_KEY) || "[]"); } catch { stored = []; }
    return stored.map((s) => ({
      ...s, status: "running", elapsedSeconds: Math.round((Date.now() - s.startedAt) / 1000),
      storeRequest: null, deciding: false, expanded: false,
    }));
  });
  const productInputWrapperRef = useRef(null);
  const suggestionDebounceRef = useRef(null);
  const suggestionRequestRef = useRef(null);
  const pollingJobIdsRef = useRef(new Set());
  const [resumingJobId, setResumingJobId] = useState(null);

  const COMMON_UOM_CODES = ["EA", "KGM", "MTR", "LTR", "PC", "SET", "BOX", "TO"];

  useEffect(() => {
    if (suggestionDebounceRef.current) clearTimeout(suggestionDebounceRef.current);
    const q = materialId.trim();
    if (q.length < 2) {
      setProductSuggestions([]);
      return;
    }
    suggestionDebounceRef.current = setTimeout(async () => {
      const requestId = q;
      suggestionRequestRef.current = requestId;
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q, limit: 10 } });
        if (suggestionRequestRef.current === requestId) setProductSuggestions(data);
      } catch {
        if (suggestionRequestRef.current === requestId) setProductSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(suggestionDebounceRef.current);
  }, [materialId]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (productInputWrapperRef.current && !productInputWrapperRef.current.contains(e.target)) {
        setShowProductSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // selectedSosKey === "" means "nothing picked yet" - Number("") is 0,
  // which would silently resolve to sosOptions[0] and defeat the entire
  // point of forcing an explicit choice when there are 2+ options (a real
  // bug found by testing: a manually-typed Site was enough to submit an
  // order bound to option[0]'s Production Model with nothing ever picked).
  const selectedSosOption = selectedSosKey === "" ? null : sosOptions[Number(selectedSosKey)];

  const chooseSosOption = (key) => {
    setSelectedSosKey(key);
    const option = sosOptions[Number(key)];
    if (option) { setSiteId(option.site_id); setSiteAutoFilled(true); } // model determines site in SAP, not the other way around
    setBomStockStatus(null); // different model may mean a different real BOM - stale panel data would be misleading
  };

  const fetchBomStock = async (live) => {
    const product = materialId.trim();
    if (!product) return;
    setBomStockLoading(true);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/bom-stock-status`, {
        params: {
          main_output_product: product,
          production_model_uuid: selectedSosOption ? selectedSosOption.production_model_uuid : undefined,
          live: !!live,
        },
      });
      setBomStockStatus(data);
    } catch {
      toast.error(live ? "Failed to check live BOM component stock - try again in a moment" : "Failed to load BOM component stock");
    } finally {
      setBomStockLoading(false);
    }
  };

  const openBomStockPanel = () => {
    setShowBomPanel(true);
    setBomStockSearch("");
    setBomStockShortageOnly(false);
    setExpandedBomComponents(new Set());
    if (!bomStockStatus) fetchBomStock(false);
  };

  // Flattened, ready-to-render rows (one per component-location, or one
  // placeholder row for a component with no stock anywhere) - shared by
  // Filterable/searchable/sortable, grouped-by-component list (Aug 2026,
  // user's explicit ask) - shared by both the modal's <table> and the
  // Excel/CSV export below so exporting always matches what's on screen.
  // "Not Assigned" is SAP's default/USABLE stock status, so per the
  // user's explicit ask it's hidden rather than shown as noise - only a
  // real exception status (Inspection, Blocked, etc.) is surfaced.
  const [bomStockSearch, setBomStockSearch] = useState("");
  const [bomStockShortageOnly, setBomStockShortageOnly] = useState(false);
  const [expandedBomComponents, setExpandedBomComponents] = useState(new Set());

  const toggleBomComponentExpand = (productId) => {
    setExpandedBomComponents((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };

  const bomStockComponents = useMemo(() => {
    if (!bomStockStatus?.checked) return [];
    const q = bomStockSearch.trim().toLowerCase();
    let list = bomStockStatus.components.map((c) => {
      const requiredQty = Math.round(c.bom_qty_per_unit * (Number(quantity) || 0) * 1e4) / 1e4;
      const shortfall = Math.max(0, Math.round((requiredQty - c.total_usable_qty) * 1e4) / 1e4);
      return { ...c, requiredQty, shortfall, sufficient: requiredQty <= 0 || shortfall <= 0 };
    });
    if (q) list = list.filter((c) => c.product_id.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q));
    if (bomStockShortageOnly) list = list.filter((c) => !c.sufficient);
    return [...list].sort((a, b) => {
      if (a.sufficient !== b.sufficient) return a.sufficient ? 1 : -1; // shortages first
      return a.product_id.localeCompare(b.product_id);
    });
  }, [bomStockStatus, quantity, bomStockSearch, bomStockShortageOnly]);

  const downloadBomStockExcel = () => {
    if (bomStockComponents.length === 0) return;
    const rows = [["Component", "Description", "Available", "Required", "Shortfall", "Unit", "Site", "Warehouse", "Stock Status", "Location Qty"]];
    bomStockComponents.forEach((c) => {
      const locs = c.locations.length > 0 ? c.locations : [{ site: "", warehouse: "", stock_status: "", qty: "" }];
      locs.forEach((loc) => {
        const isDefaultStatus = (loc.stock_status || "").trim().toLowerCase() === "not assigned";
        rows.push([
          c.product_id, c.description || "", c.total_usable_qty, c.requiredQty, c.shortfall, c.unit_of_measure || "",
          loc.site || "", loc.warehouse || "", isDefaultStatus ? "" : (loc.stock_status || ""), loc.qty ?? "",
        ]);
      });
    });
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet["!cols"] = [{ wch: 16 }, { wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 6 }, { wch: 24 }, { wch: 24 }, { wch: 14 }, { wch: 12 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "BOM Stock");
    XLSX.writeFile(workbook, `bom-stock-${materialId.trim() || "component"}-x${quantity || 0}.xlsx`);
  };

  const [lastCheckedId, setLastCheckedId] = useState(null);
  const [sosCheckFailed, setSosCheckFailed] = useState(false);
  const [sosCheckFailReason, setSosCheckFailReason] = useState(null);
  const [sosRefreshing, setSosRefreshing] = useState(false);

  const checkSourceOfSupply = async (idOverride) => {
    const id = (idOverride ?? materialId).trim();
    if (!id || (id === lastCheckedId && !sosCheckFailed)) return;  // avoid redundant re-lookups (e.g. blur right before a submit click) that would reset materialUuid/site mid-flow - unless the last attempt failed and this is a retry
    setMaterialId(id);
    setLastCheckedId(id);
    setShowProductSuggestions(false);
    setSosLoading(true);
    setSosCheckFailed(false);
    setSosCheckFailReason(null);
    setSosOptions([]);
    setSelectedSosKey("");
    setMaterialUuid(null);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/source-of-supply-options/${encodeURIComponent(id)}`);
      setMaterialUuid(data.material_uuid || null);
      const options = data.options || [];
      setSosOptions(options);
      // Only auto-pick when there's exactly ONE valid Model/Site combo -
      // no real ambiguity there. With 2+ valid options, force the user to
      // explicitly choose (previously defaulted to the first "Active" one
      // alphabetically, which could silently pick a model whose BOM has a
      // stock shortage while a perfectly fine alternative sat unused in
      // the dropdown - user's explicit call, no more auto-pick when there's
      // a real choice to make).
      if (options.length === 1) {
        setSelectedSosKey("0");
        setSiteId(options[0].site_id); // model determines site - always overwritten here, never an independent pre-filter
        setSiteAutoFilled(true);
      } else if (siteAutoFilled) {
        setSiteId(""); // clear a stale auto-filled site from a previous material - but never clobber a site the user typed themselves
        setSiteAutoFilled(false);
      }
      // Base UoM (SAP's real base unit for this material, exposed Aug
      // 2026) - lock the picker to it, same UX pattern as Site locking
      // from the chosen Production Model, so the previously-free UoM
      // choice can no longer silently mismatch SAP's own unit.
      if (data.base_uom) {
        setUnitCode(data.base_uom);
        setUnitCodeAutoFilled(true);
      } else if (unitCodeAutoFilled) {
        setUnitCode("EA");
        setUnitCodeAutoFilled(false);
      }
    } catch (e) {
      // A transient network/5xx error is NOT the same as "material doesn't
      // exist" - don't show the red not-recognized warning or block submit
      // for a real product just because one lookup attempt failed.
      // Oct 3 2026: when the backend knows WHY (e.g. SAP refused its OData
      // login), show that instead of guessing "network hiccup" - a wrong
      // explanation sent the user hunting for a Product ID problem that
      // never existed.
      setSosOptions([]);
      setSosCheckFailed(true);
      setSosCheckFailReason(e?.response?.data?.detail || null);
    } finally {
      setSosLoading(false);
      setSosChecked(true);
    }
  };

  // "Refresh Models" (Sep 11 2026, user's explicit ask): the custom SAP
  // OData service backing Source of Supply can lag minutes-to-hours
  // behind a just-created/released Production Model (confirmed live -
  // not a bug in this app's own filtering, SAP's own raw response was
  // missing it too). checkSourceOfSupply's own dedupe guard
  // (`id === lastCheckedId`) would otherwise skip a re-fetch of the
  // SAME Product ID - clearing it here forces a real fresh SAP call.
  const refreshSourceOfSupply = async () => {
    if (!materialId.trim()) return;
    setSosRefreshing(true);
    setLastCheckedId(null);
    try {
      await checkSourceOfSupply(materialId);
    } finally {
      setSosRefreshing(false);
    }
  };

  const loadHistory = useCallback(() => {
    setLoadingHistory(true);
    axios.get(`${API}/production-confirmation/proposal-history`).then(({ data }) => setHistory(data.entries)).catch(() => toast.error("Failed to load Proposal/Release history")).finally(() => setLoadingHistory(false));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const removeActiveJob = (jobId) => setActiveJobs((prev) => prev.filter((j) => j.job_id !== jobId));
  const toggleJobExpand = (jobId) => setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, expanded: !j.expanded } : j)));

  // "Resume" action (Aug 27 2026, user's explicit ask) on a failed order
  // job that already has a known SAP Proposal ID - hands that ID + the
  // original form snapshot back to the backend, which resumes the
  // Proposal -> Order -> Release pipeline under a fresh job_id instead of
  // requiring a trip to the SAP UI to finish it by hand.
  const resumeFailedJob = async (job) => {
    setResumingJobId(job.job_id);
    try {
      const { data } = await axios.post(`${API}/production-confirmation/create-and-release-order/${job.job_id}/resume`, {
        actor: actorName.trim(),
      });
      removeActiveJob(job.job_id);
      setActiveJobs((prev) => [...prev, {
        job_id: data.job_id, material_id: job.material_id, site_id: job.site_id,
        quantity: job.quantity, unit_code: job.unit_code, status: "waiting_for_order",
        startedAt: Date.now(), elapsedSeconds: 0,
      }]);
      pollJob(data.job_id);
      toast.success(`Resuming from Proposal ${data.production_proposal_id} - no need to use the SAP UI`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume order creation");
    } finally {
      setResumingJobId(null);
    }
  };

  // "Retry Now" (Aug 2026, user's explicit ask) - lets an impatient user
  // fire the Release trigger immediately instead of waiting out the
  // background job's own ~90s retrigger cycle. The background loop keeps
  // running/re-triggering on its own schedule regardless of this call's
  // outcome, so this can never make things worse.
  const forceRetrigger = async (jobId) => {
    setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, retrying: true } : j)));
    try {
      const { data } = await axios.post(`${API}/production-confirmation/create-and-release-order/${jobId}/force-retrigger`);
      if (data.success) toast.success("SAP accepted the retry - still watching for the resulting Order");
      else toast.error(data.error || "SAP rejected the retry - it will keep auto-retrying in the background");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry");
    } finally {
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, retrying: false } : j)));
    }
  };

  // Runs entirely independently per job - multiple can be in flight at
  // once, each polling its own status on its own timer, none of them
  // blocking the form above from starting yet another order.
  const pollJob = useCallback((jobId) => {
    if (pollingJobIdsRef.current.has(jobId)) return;
    pollingJobIdsRef.current.add(jobId);
    const tickTimer = setInterval(() => {
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, elapsedSeconds: Math.round((Date.now() - j.startedAt) / 1000) } : j)));
    }, 1000);
    let lastStoreReqKey = null;
    (async () => {
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          let job;
          try {
            ({ data: job } = await axios.get(`${API}/production-confirmation/create-and-release-order/status/${jobId}`));
          } catch (e) {
            if (e.response?.status === 404) {
              // Real bug reproduced live (Aug 2026): a genuinely-gone job
              // (e.g. a stale localStorage entry from a much older
              // session whose Mongo doc no longer exists) used to be
              // treated the same as a transient network hiccup below,
              // so this loop retried FOREVER and the row's spinner never
              // stopped, no matter what the user clicked. A 404 here is
              // permanent, not transient - stop tracking it for good.
              toast.error(`Lost track of this order (job no longer exists) - it may already be done; check History`);
              removeActiveJob(jobId);
              break;
            }
            continue; // transient network hiccup - keep tracking this job, try again next round
          }
          if (PAUSED_STATUSES.includes(job.status)) {
            const key = `${job.store_request_id}:${job.status}`;
            if (job.store_request_id && lastStoreReqKey !== key) {
              try {
                const { data: reqDoc } = await axios.get(`${API}/production-confirmation/store-requests/by-job/${jobId}`);
                lastStoreReqKey = key;
                setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: job.status, storeRequest: reqDoc } : j)));
              } catch {
                setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: job.status } : j)));
              }
            } else {
              setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: job.status } : j)));
            }
            continue;
          }
          setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? {
            ...j, status: job.status,
            productionProposalId: job.production_proposal_id || j.productionProposalId,
            productionOrderId: job.production_order_id || j.productionOrderId,
            orderReleaseState: job.order_release_state,
            orderReleaseAttempts: job.order_release_attempts,
            orderReleaseError: job.order_release_error,
            nextReleaseAttemptAt: job.next_release_attempt_at,
            releaseTriggerCount: job.release_trigger_count,
            lastReleaseTriggerOk: job.last_release_trigger_ok,
            lastReleaseTriggerError: job.last_release_trigger_error,
          } : j)));
          if (job.status === "done") {
            const result = job.result;
            if (result.production_order_id && result.released) {
              toast.success(`Production Order ${result.production_order_id} created and released in SAP - ready for confirmation`);
            } else if (result.production_order_id) {
              toast.error(`Order ${result.production_order_id} was created but Release failed - use the fallback form to retry`);
            } else {
              toast.error(result.note || "Proposal created but the Order hasn't appeared yet - it keeps retrying automatically, check history shortly");
            }
            removeActiveJob(jobId);
            loadHistory();
            break;
          }
          if (job.status === "paused_sap_block") {
            const block = job.result?.sap_block || {};
            setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? {
              ...j, status: "paused_sap_block", error: block.message || "SAP refused to release this order.",
              failure: job.result, sapError: block.sap_error,
            } : j)));
            toast.error("SAP refused this order - fix it in SAP, then Retry", { duration: 8000 });
            loadHistory();
            break;
          }
          if (job.status === "cancelled") {
            toast.error(job.result?.note || "Order creation was cancelled after the store approval decision");
            removeActiveJob(jobId);
            loadHistory();
            break;
          }
          if (job.status === "failed") {
            // SFG-shortage block (Aug 2026, user's explicit ask): keep this
            // row visible with a persistent, dismissible detail table
            // instead of a toast that vanishes in a few seconds - a real
            // "which component, how short" needs to stay on screen until
            // the planner has acted on it.
            if (job.result?.reason === "sfg_shortage") {
              setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: "failed", failure: job.result, error: job.error } : j)));
              break;
            }
            // Aug 27 2026 fix (user's explicit ask - "if an error happens
            // during production order creation ... it would be ideal to
            // have a recovery on app frontend"): any OTHER mid-pipeline
            // failure used to just toast + vanish, forcing a trip to the
            // SAP UI to find and finish a Proposal that may already
            // exist there. Now it stays on screen with the real reason
            // and a "Resume" action when a Proposal ID is known.
            setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: "failed", failure: job.result, error: job.error } : j)));
            break;
          }
        }
      } finally {
        clearInterval(tickTimer);
        pollingJobIdsRef.current.delete(jobId);
      }
    })();
  }, [loadHistory]);

  // Persist the (small) seed info for every active job so a page refresh
  // doesn't lose track of orders still running in the background. Safe to
  // run on every render including the very first (it just re-writes back
  // the same data `activeJobs` was hydrated from above) - no skip-guard
  // needed now that hydration itself happens synchronously in useState.
  useEffect(() => {
    const seed = activeJobs.map(({ job_id, material_id, site_id, quantity, unit_code, startedAt }) => ({ job_id, material_id, site_id, quantity, unit_code, startedAt }));
    localStorage.setItem(ACTIVE_JOBS_STORAGE_KEY, JSON.stringify(seed));
  }, [activeJobs]);

  // Kick off polling once for whatever was hydrated above - pollJob's own
  // pollingJobIdsRef guard makes this safe even under StrictMode's dev
  // double-invoke of mount effects.
  useEffect(() => {
    activeJobs.forEach((j) => pollJob(j.job_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refreshingSfgStock) {
      setRefreshSfgElapsed(0);
      return;
    }
    const interval = setInterval(() => setRefreshSfgElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [refreshingSfgStock]);

  const refreshLiveSfgStock = async (siteOverride) => {
    const site = (siteOverride || siteId).trim();
    if (!site) {
      toast.error("Enter a Site first");
      return;
    }
    setRefreshingSfgStock(true);
    setRefreshSfgStatus(`Pulling live SFG stock for ${site} from SAP...`);
    try {
      const { data } = await axios.post(`${API}/production-confirmation/refresh-live-sfg-stock`, null, { params: { site_id: site } });
      let job = null;
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        const res = await axios.get(`${API}/production-confirmation/refresh-live-sfg-stock/${data.job_id}`);
        job = res.data;
        if (job.status === "done" || job.status === "failed") break;
      }
      if (job?.status === "done") {
        toast.success(`Live SFG stock refreshed for ${site} - retry creating the order now`);
      } else {
        toast.error(job?.error || "Failed to refresh live SFG stock");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh live SFG stock");
    } finally {
      setRefreshingSfgStock(false);
      setRefreshSfgStatus(null);
    }
  };

  const createProposal = async () => {
    if (sosOptions.length > 1 && !selectedSosOption) {
      toast.error("This material has multiple valid Production Models - pick one from the Source of Supply list before creating the order");
      return;
    }
    if (!materialId.trim() || !siteId.trim() || !quantity) {
      toast.error("Product, Site and Quantity are required");
      return;
    }
    if (Number(quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    if (sosLoading) {
      toast.error("Still checking this Product ID with SAP - wait a moment and try again");
      return;
    }
    if (sosChecked && !materialUuid && !sosCheckFailed) {
      toast.error("Product ID not recognized in SAP - pick one from the suggestions or check the spelling");
      return;
    }
    setSubmitting(true);
    setValuationBlock(null);
    try {
      const payloadMaterialId = materialId.trim();
      const payloadSiteId = siteId.trim().toUpperCase();
      const payloadQuantity = Number(quantity);
      const payloadUnitCode = unitCode.trim().toUpperCase() || "EA";
      const { data } = await axios.post(`${API}/production-confirmation/create-and-release-order`, {
        material_id: payloadMaterialId,
        site_id: payloadSiteId,
        quantity: payloadQuantity,
        unit_code: payloadUnitCode,
        availability_datetime: requestedEndDate ? new Date(requestedEndDate).toISOString() : null,
        actor: actorName.trim(),
        logistic_relationship_uuid: selectedSosOption ? selectedSosOption.logistic_relationship_uuid : null,
        production_model_uuid: selectedSosOption ? selectedSosOption.production_model_uuid : null,
        production_model_id: selectedSosOption ? selectedSosOption.production_model_id : null,
      });
      const jobId = data.job_id;
      setValuationBlock(data.valuation_note ? { kind: "warning", message: data.valuation_note } : null);
      setActiveJobs((prev) => [{
        job_id: jobId, material_id: payloadMaterialId, site_id: payloadSiteId, quantity: payloadQuantity, unit_code: payloadUnitCode,
        status: "checking_stock", startedAt: Date.now(), elapsedSeconds: 0, storeRequest: null, deciding: false, expanded: false,
      }, ...prev]);
      pollJob(jobId);
      toast.success("Order request submitted - track its progress in the Active Orders table below.");
      // Free up the form immediately so another order can be submitted
      // right away - it no longer waits for this one to finish/pause.
      setMaterialId(""); setQuantity("1"); setRequestedEndDate(""); setSiteId("");
      setSosOptions([]); setSosChecked(false); setSelectedSosKey(""); setSiteAutoFilled(false);
      setUnitCode("EA"); setUnitCodeAutoFilled(false); setMaterialUuid(null); setLastCheckedId(null);
      setShowBomPanel(false); setBomStockStatus(null);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Failed to create Production Order in SAP";
      if (e.response?.status === 400) setValuationBlock({ kind: "error", message: detail });
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const decideStoreRequest = async (jobId, requestId, decision) => {
    if (!requestId) return;
    setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, deciding: true } : j)));
    try {
      await axios.post(`${API}/production-confirmation/store-requests/${requestId}/decision`, {
        decision, actor: actorName.trim(),
      });
      toast.success(decision === "approve" ? "Approved - resuming automated order creation" : "Rejected - order creation cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record your decision");
    } finally {
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, deciding: false } : j)));
    }
  };

  // "Stop the rotating wheel" (Aug 2026): stops THIS app's own polling for
  // the resulting Order - it can never delete/cancel the SAP Proposal
  // already created (no such SAP API exists), so that stays exactly as
  // it is, un-converted, in SAP. pollJob's existing "cancelled" handling
  // toasts + removes the row once the backend confirms the stop.
  const stopTrackingJob = async (jobId) => {
    setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, stopping: true } : j)));
    try {
      await axios.post(`${API}/production-confirmation/create-and-release-order/${jobId}/cancel`);
      // The backend only checks this flag at specific checkpoints (stock
      // check, proposal creation, the order/release poll loop) - give it
      // a reasonable window to land before re-enabling the button, so a
      // Stop clicked at an awkward moment doesn't leave "Stopping..."
      // disabled forever with no feedback (pollJob's own "cancelled"
      // handling removes the row well before this if it lands sooner).
      setTimeout(() => {
        setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId && j.stopping ? { ...j, stopping: false } : j)));
      }, 30000);
    } catch (e) {
      if (e.response?.status === 404) {
        // Same stale-job case as pollJob's 404 handling above - nothing
        // to actually stop, so just drop it from the list instead of
        // leaving the row spinning forever on a job that's already gone.
        toast.error("This order no longer exists - removing it from the list");
        removeActiveJob(jobId);
        return;
      }
      toast.error(e.response?.data?.detail || "Failed to stop this order run");
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, stopping: false } : j)));
    }
  };

  const releaseOrder = async () => {
    if (!releaseOrderId.trim()) {
      toast.error("Enter the Production Order ID to release");
      return;
    }
    setReleasing(true);
    try {
      await axios.post(`${API}/production-confirmation/release-order`, {
        production_order_id: releaseOrderId.trim(),
        actor: actorName.trim(),
      });
      toast.success(`Production Order ${releaseOrderId.trim()} released in SAP`);
      setReleaseOrderId("");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to release Production Order in SAP");
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="create-proposal-card">
          <h3 className="font-heading text-sm font-bold text-[#1D2939] uppercase tracking-wide">Create Production Order</h3>
          <p className="text-xs text-[#667085]">Creates and releases a Production Order in SAP - fully automated, retries in the background until SAP converts it. Checks component stock first; submitting an order tracks it in the Active Orders table below so this form stays free to submit another right away.</p>
          <div ref={productInputWrapperRef} className="relative">
            <Label className="text-xs font-bold text-[#344054]">Product ID</Label>
            <Input
              value={materialId}
              onChange={(e) => { setMaterialId(e.target.value); setSosChecked(false); setSosOptions([]); setMaterialUuid(null); setLastCheckedId(null); setShowProductSuggestions(true); setBomStockStatus(null); }}
              onFocus={() => setShowProductSuggestions(true)}
              onBlur={() => checkSourceOfSupply()}
              placeholder="e.g. MAZ42117272-TA"
              data-testid="create-proposal-product-input"
              autoComplete="off"
            />
            {showProductSuggestions && productSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-[#D0D5DD] rounded-sm shadow-md max-h-56 overflow-y-auto" data-testid="product-suggestions-dropdown">
                {productSuggestions.map((s) => (
                  <button
                    key={s.product_id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9FAFB] border-b border-[#EAECF0] last:border-0"
                    onMouseDown={(e) => { e.preventDefault(); checkSourceOfSupply(s.product_id); }}
                    data-testid={`product-suggestion-${s.product_id}`}
                  >
                    <div className="font-medium text-[#1D2939]">{s.product_id}</div>
                    {s.description && <div className="text-xs text-[#667085]">{s.description}</div>}
                  </button>
                ))}
              </div>
            )}
            {sosChecked && !sosLoading && !materialUuid && !sosCheckFailed && (
              <p className="text-xs text-[#B42318] mt-1" data-testid="product-not-recognized-warning">
                Product ID not recognized in SAP - pick a suggestion above or check the spelling.
              </p>
            )}
            {sosChecked && !sosLoading && materialUuid && sosOptions.length === 0 && (
              <p className="text-xs text-[#B54708] mt-1" data-testid="no-production-model-warning">
                Product recognized in SAP, but no Production Model/BOM is set up for it yet at any Site - set one up in SAP first, or type a Site manually below if you know one already exists.
              </p>
            )}
            {sosCheckFailed && (
              <p className="text-xs text-[#B54708] mt-1" data-testid="product-lookup-failed-warning">
                {sosCheckFailReason || "Could not verify this Product ID with SAP just now (network hiccup) - you can still proceed, or blur the field again to retry."}
              </p>
            )}
          </div>
          <div className="min-h-[26px]">
            <div className="flex items-center justify-between gap-2">
              {sosLoading && <p className="text-xs text-[#667085]" data-testid="sos-loading-text">Checking available Production Models...</p>}
              {materialId.trim() && !sosLoading && (
                <button
                  type="button"
                  onClick={refreshSourceOfSupply}
                  disabled={sosRefreshing}
                  className="text-xs text-[#7A1E1E] font-bold hover:underline disabled:opacity-50 flex items-center gap-1"
                  data-testid="refresh-production-models-button"
                  title="Re-check SAP for the latest Production Models - useful right after creating/releasing one in SAP, which can take a few minutes to show up here"
                >
                  <ArrowClockwise size={12} className={sosRefreshing ? "animate-spin" : ""} />
                  {sosRefreshing ? "Refreshing..." : "Refresh Models"}
                </button>
              )}
            </div>
            {sosChecked && sosOptions.length > 0 && (
              <div data-testid="source-of-supply-picker">
                <Label className="text-xs font-bold text-[#344054]">Source of Supply (Production Model)</Label>
                <Select value={selectedSosKey} onValueChange={chooseSosOption}>
                  <SelectTrigger data-testid="source-of-supply-select-trigger">
                    <SelectValue placeholder="Choose a Production Model" />
                  </SelectTrigger>
                  <SelectContent>
                    {sosOptions.map((o, idx) => (
                      <SelectItem key={`${o.production_model_id}-${o.site_id}`} value={String(idx)} data-testid={`source-of-supply-option-${o.production_model_id}-${o.site_id}`}>
                        {o.production_model_id}{o.description ? ` — ${o.description}` : ""} (Site {o.site_id}){o.is_active ? "" : " (Obsolete)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p
                  className={`text-xs mt-1 ${sosOptions.length > 1 && !selectedSosOption ? "font-bold text-[#B54708]" : "text-[#667085]"}`}
                  data-testid={sosOptions.length > 1 && !selectedSosOption ? "sos-choice-required-warning" : undefined}
                >
                  {sosOptions.length > 1
                    ? selectedSosOption
                      ? `Using ${selectedSosOption.production_model_id} (Site ${selectedSosOption.site_id}).`
                      : `This material has ${sosOptions.length} valid Production Model/Site combinations - you must pick one before creating the order (it sets the Site for you; not auto-picked since one option may be short on stock while another isn't).`
                    : "Picking this confirms the Production Model - and its Site - SAP will use."}
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#344054]">Site</Label>
              <Input
                value={siteId}
                onChange={(e) => { setSiteId(e.target.value.toUpperCase()); setSiteAutoFilled(false); }}
                placeholder="e.g. P2"
                disabled={siteAutoFilled}
                data-testid="create-proposal-site-input"
              />
              {siteAutoFilled && <p className="text-[11px] text-[#667085] mt-0.5">Locked - set by the chosen Production Model above</p>}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={refreshingSfgStock || !siteId.trim()}
                onClick={() => refreshLiveSfgStock()}
                className="mt-1.5 h-7 text-[11px]"
                data-testid="refresh-live-sfg-stock-button"
              >
                <ArrowClockwise size={12} className={`mr-1 ${refreshingSfgStock ? "animate-spin" : ""}`} />
                {refreshingSfgStock ? `Refreshing (${refreshSfgElapsed}s)...` : "Refresh Live SFG Stock"}
              </Button>
              {refreshingSfgStock && refreshSfgStatus && (
                <p className="text-[11px] text-[#667085] mt-0.5" data-testid="refresh-live-sfg-stock-status">{refreshSfgStatus}</p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">UoM</Label>
              <Select value={unitCode} onValueChange={setUnitCode} disabled={unitCodeAutoFilled}>
                <SelectTrigger data-testid="create-proposal-uom-select-trigger">
                  <SelectValue placeholder="EA" />
                </SelectTrigger>
                <SelectContent>
                  {(COMMON_UOM_CODES.includes(unitCode) ? COMMON_UOM_CODES : [unitCode, ...COMMON_UOM_CODES]).map((code) => (
                    <SelectItem key={code} value={code} data-testid={`create-proposal-uom-option-${code}`}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {unitCodeAutoFilled ? (
                <p className="text-[11px] text-[#667085] mt-0.5" data-testid="uom-locked-helper">Locked - SAP's base unit for this material</p>
              ) : unitCode !== "EA" && (
                <p className="text-[11px] text-[#B54708] mt-0.5" data-testid="uom-not-ea-warning">
                  Not verified against SAP's base unit for this material yet - double-check it's correct before submitting.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#344054]">Quantity</Label>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="create-proposal-qty-input" />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Requested End Date</Label>
              <Input type="date" value={requestedEndDate} onChange={(e) => setRequestedEndDate(e.target.value)} data-testid="create-proposal-date-input" />
            </div>
          </div>
          {valuationBlock && (
            <Alert
              className={valuationBlock.kind === "error" ? "rounded-sm bg-[#FEF3F2] border-[#FECDCA]" : "rounded-sm bg-[#FFFAEB] border-[#FEDF89]"}
              data-testid="valuation-check-banner"
            >
              <WarningCircle size={16} className={valuationBlock.kind === "error" ? "text-[#B42318]" : "text-[#B54708]"} />
              <AlertDescription className={`text-xs ${valuationBlock.kind === "error" ? "text-[#B42318]" : "text-[#B54708]"}`}>
                {valuationBlock.message}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button onClick={createProposal} disabled={submitting} className="flex-1" data-testid="create-proposal-submit-button">
              {submitting ? "Submitting..." : "Create Production Order"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!materialId.trim()}
              onClick={openBomStockPanel}
              data-testid="toggle-bom-stock-panel-button"
            >
              Check Stock
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="release-order-card">
          <h3 className="font-heading text-sm font-bold text-[#1D2939] uppercase tracking-wide">Fallback: Release Order Manually</h3>
          <p className="text-xs text-[#667085]">Only needed if the automatic flow above times out. Enter a Production Order ID (once visible in SAP) to release it.</p>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Production Order ID</Label>
            <Input value={releaseOrderId} onChange={(e) => setReleaseOrderId(e.target.value)} placeholder="e.g. 69843" data-testid="release-order-id-input" />
          </div>
          <Button onClick={releaseOrder} disabled={releasing} className="w-full" data-testid="release-order-submit-button">
            {releasing ? "Releasing in SAP..." : "Release Production Order"}
          </Button>
        </div>
      </div>

      <Dialog open={showBomPanel} onOpenChange={(open) => setShowBomPanel(open)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="bom-stock-status-panel">
          <DialogHeader>
            <DialogTitle>BOM Component Stock (x{quantity || 0} {formatUnit(unitCode)})</DialogTitle>
            <DialogDescription>
              {materialId.trim()} - every site/warehouse this BOM's components sit in, scaled to the Quantity you entered.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {bomStockStatus && (
              <span className="text-xs text-[#667085]" data-testid="bom-stock-status-source">
                Source: {bomStockStatus.source === "live" ? "Live SAP" : "Cached snapshot"}{bomStockStatus.fetched_at ? ` · as of ${new Date(bomStockStatus.fetched_at).toLocaleString("en-IN")}` : ""}
                {bomStockStatus.bom_id ? ` · BOM ${bomStockStatus.bom_id}` : ""}
              </span>
            )}
            {bomStockStatus?.superseded_bom_id && (
              <span className="text-xs text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2 py-1 w-full" data-testid="bom-stock-superseded-note">
                The production model in SAP still points at BOM <strong>{bomStockStatus.superseded_bom_id}</strong>, which SAP itself marks
                inconsistent - these components come from <strong>{bomStockStatus.bom_id}</strong>, the current BOM for this product.
                Re-link the model to it in SAP so the production order consumes the right input.
              </span>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={bomStockLoading || !materialId.trim()}
                onClick={() => fetchBomStock(true)}
                data-testid="bom-stock-status-refresh-button"
              >
                <ArrowClockwise size={12} className={`mr-1.5 ${bomStockLoading ? "animate-spin" : ""}`} />
                {bomStockLoading ? "Checking..." : "Check Live Stock"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={bomStockComponents.length === 0}
                onClick={downloadBomStockExcel}
                data-testid="bom-stock-status-download-button"
              >
                Download Excel
              </Button>
            </div>
          </div>

          {bomStockLoading && !bomStockStatus && (
            <p className="text-sm text-[#667085]" data-testid="bom-stock-status-loading">Checking BOM component stock...</p>
          )}

          {bomStockStatus?.checked && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                  <Input
                    value={bomStockSearch}
                    onChange={(e) => setBomStockSearch(e.target.value)}
                    placeholder="Search by component ID or description..."
                    className="h-8 pl-7 text-xs"
                    data-testid="bom-stock-search-input"
                  />
                </div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-[#344054] cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={bomStockShortageOnly}
                    onChange={(e) => setBomStockShortageOnly(e.target.checked)}
                    data-testid="bom-stock-shortage-only-toggle"
                  />
                  Shortages only
                </label>
              </div>

              <div className="flex-1 overflow-auto border border-[#EAECF0] rounded-sm">
                <table className="w-full text-xs border-collapse" data-testid="bom-stock-status-table">
                  <thead className="sticky top-0">
                    <tr>
                      {["Component", "Description", "Available", "Required", "Shortfall", "Site", "Warehouse", "Stock Status", "Location Qty"].map((h) => (
                        <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bomStockComponents.map((c) => {
                      const expanded = expandedBomComponents.has(c.product_id);
                      const hasLocations = c.locations.length > 0;
                      return (
                        <Fragment key={c.product_id}>
                          <tr className={c.sufficient ? "bg-white" : "bg-[#FEF3F2]"} data-testid={`bom-stock-component-row-${c.product_id}`}>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">
                              <button
                                type="button"
                                className="flex items-center gap-1 disabled:cursor-default"
                                disabled={!hasLocations}
                                onClick={() => toggleBomComponentExpand(c.product_id)}
                                data-testid={`bom-stock-expand-${c.product_id}`}
                              >
                                {hasLocations ? (expanded ? <CaretDown size={11} className="shrink-0" /> : <CaretRight size={11} className="shrink-0" />) : <span className="inline-block w-[11px]" />}
                                {c.product_id}
                              </button>
                            </td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5">{c.description || "—"}</td>
                            <td className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${c.sufficient ? "text-[#027A48]" : "text-[#B42318] font-bold"}`}>
                              {formatQty(c.total_usable_qty)} {formatUnit(c.unit_of_measure)}
                            </td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(c.requiredQty)} {formatUnit(c.unit_of_measure)}</td>
                            <td className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${!c.sufficient ? "text-[#B42318] font-bold" : ""}`}>
                              {c.sufficient ? "—" : `${formatQty(c.shortfall)} ${formatUnit(c.unit_of_measure)}`}
                            </td>
                            <td colSpan={4} className="border border-[#D0D5DD] px-2 py-1.5 text-[#667085]">
                              {hasLocations ? `${c.locations.length} location${c.locations.length > 1 ? "s" : ""} - click to ${expanded ? "collapse" : "expand"}` : "No stock found anywhere"}
                            </td>
                          </tr>
                          {expanded && c.locations.map((loc, li) => {
                            const isDefaultStatus = (loc.stock_status || "").trim().toLowerCase() === "not assigned";
                            return (
                              <tr key={li} className="bg-[#F9FAFB]" data-testid={`bom-stock-location-row-${c.product_id}-${li}`}>
                                <td className="border border-[#D0D5DD] px-2 py-1.5" colSpan={5}></td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5">{loc.site || "—"}</td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5">{loc.warehouse || "—"}</td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5">{isDefaultStatus ? "—" : (loc.stock_status || "—")}</td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(loc.qty)} {formatUnit(c.unit_of_measure)}</td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                    {bomStockComponents.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]">
                        {bomStockSearch.trim() || bomStockShortageOnly ? "No components match your search/filter." : "No active components in this BOM."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {bomStockStatus && !bomStockStatus.checked && (
            <p className="text-sm text-[#98A2B3]" data-testid="bom-stock-status-unavailable">{bomStockStatus.reason}</p>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto" data-testid="active-orders-card">
        <div className="px-3 py-2 border-b border-[#D0D5DD] bg-[#F9FAFB]">
          <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">Active Orders ({activeJobs.length})</h3>
        </div>
        <table className="w-full text-[12px] border-collapse" data-testid="active-orders-table">
          <thead>
            <tr>
              {["Started", "Material", "Site", "Qty", "Status", "Actions"].map((h) => (
                <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeJobs.map((j, i) => {
              const isPaused = PAUSED_STATUSES.includes(j.status);
              return (
                <Fragment key={j.job_id}>
                  <tr className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`active-order-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{new Date(j.startedAt).toLocaleTimeString("en-IN")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">{j.material_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{j.site_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(j.quantity)} {formatUnit(j.unit_code)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`active-order-status-${i}`}>
                      {isPaused ? (
                        <>
                          <Badge variant="outline" className={`border ${
                            j.status === "partial_pending_planner" ? "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]"
                            : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
                          }`}>
                            {PHASE_LABELS[j.status] || j.status}
                          </Badge>
                        </>
                      ) : j.status === "paused_sap_block" ? (
                        <Badge variant="outline" className="border bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" data-testid={`active-order-sap-block-badge-${i}`}>
                          Paused - SAP Rejected
                        </Badge>
                      ) : j.status === "failed" ? (
                        <Badge variant="outline" className="border bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">
                          {j.failure?.reason === "sfg_shortage" ? "SFG Shortage - Blocked" : "Failed"}
                        </Badge>
                      ) : (
                        <>
                          <OrderStepTracker status={j.status} elapsedSeconds={j.elapsedSeconds} />
                          {j.status === "waiting_for_order" && j.releaseTriggerCount > 0 && (
                            <div className="text-[11px] text-[#667085] mt-1" data-testid={`active-order-trigger-detail-${i}`}>
                              Waiting for SAP to convert Proposal {j.productionProposalId || "—"} into an Order - trigger attempt #{j.releaseTriggerCount}
                              {j.lastReleaseTriggerOk === false && j.lastReleaseTriggerError && (
                                j.lastReleaseTriggerError.toLowerCase().includes("already requested") ? (
                                  <span className="text-[#175CD3]"> (SAP already accepted an earlier attempt - confirming the resulting Order now)</span>
                                ) : (
                                  <span className="text-[#B54708]"> ({j.lastReleaseTriggerError})</span>
                                )
                              )}
                            </div>
                          )}
                          {j.productionOrderId && j.status === "releasing_order" && (
                            <OrderReleaseNote job={j} index={i} onUseOrderId={setReleaseOrderId} />
                          )}
                        </>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {j.status === "partial_pending_planner" ? (
                        <div className="flex flex-wrap gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => toggleJobExpand(j.job_id)} data-testid={`active-order-view-button-${i}`}>{j.expanded ? "Hide" : "View"}</Button>
                          <Button size="sm" disabled={j.deciding} onClick={() => decideStoreRequest(j.job_id, j.storeRequest?._id, "approve")} data-testid={`store-approval-approve-button-${i}`}>Confirm</Button>
                          <Button size="sm" variant="outline" disabled={j.deciding} onClick={() => decideStoreRequest(j.job_id, j.storeRequest?._id, "reject")} data-testid={`store-approval-reject-button-${i}`}>Delete</Button>
                        </div>
                      ) : j.status === "waiting_store_approval" ? (
                        j.storeRequest ? (
                          <Button size="sm" variant="outline" onClick={() => toggleJobExpand(j.job_id)} data-testid={`active-order-view-button-${i}`}>{j.expanded ? "Hide" : "View"}</Button>
                        ) : <span className="text-[11px] text-[#98A2B3]">Loading...</span>
                      ) : j.status === "paused_sap_block" ? (
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            disabled={resumingJobId === j.job_id}
                            onClick={() => resumeFailedJob(j)}
                            title="Re-sends the Release to SAP for the Proposal/Order that already exists - use it once the SAP master data is fixed"
                            data-testid={`active-order-retry-sap-block-button-${i}`}
                          >
                            {resumingJobId === j.job_id ? "Retrying..." : "Retry"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => removeActiveJob(j.job_id)} data-testid={`active-order-dismiss-button-${i}`}>Dismiss</Button>
                        </div>
                      ) : j.status === "failed" ? (
                        <div className="flex flex-wrap gap-1.5">
                          {j.failure?.reason !== "sfg_shortage" && j.failure?.reason !== "duplicate_store_request" && (
                            <Button
                              size="sm"
                              disabled={resumingJobId === j.job_id}
                              onClick={() => resumeFailedJob(j)}
                              title={j.failure?.production_proposal_id
                                ? "Picks up from the Proposal that already exists in SAP"
                                : "Nothing was created in SAP - runs this same order again"}
                              data-testid={`active-order-resume-button-${i}`}
                            >
                              {resumingJobId === j.job_id
                                ? (j.failure?.production_proposal_id ? "Resuming..." : "Retrying...")
                                : (j.failure?.production_proposal_id ? "Resume" : "Retry")}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => removeActiveJob(j.job_id)} data-testid={`active-order-dismiss-button-${i}`}>Dismiss</Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={j.stopping}
                            onClick={() => stopTrackingJob(j.job_id)}
                            title="Stops this app's own tracking only - if a SAP Proposal was already created, it stays in SAP un-converted, it is not deleted"
                            data-testid={`active-order-stop-button-${i}`}
                          >
                            {j.stopping ? "Stopping..." : "Stop"}
                          </Button>
                          {j.status === "waiting_for_order" && j.elapsedSeconds >= 120 && !(j.lastReleaseTriggerError || "").toLowerCase().includes("already requested") && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={j.retrying}
                              onClick={() => forceRetrigger(j.job_id)}
                              title="Fire the SAP Release trigger right now instead of waiting for the next automatic attempt"
                              data-testid={`active-order-retry-now-button-${i}`}
                            >
                              {j.retrying ? "Retrying..." : "Retry Now"}
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {j.status === "failed" && j.failure?.reason === "sfg_shortage" && (
                    <tr data-testid={`active-order-sfg-shortage-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FEF3F2]">
                        <p className="text-[11px] text-[#B42318] mb-1.5">
                          Blocked before anything was created in SAP - {j.failure.short_components.length} sub-assembly component(s) short at {j.failure.site_id}.
                          These are produced in-house, not stocked by the Store.
                        </p>
                        <table className="w-full text-[11px] border-collapse bg-white">
                          <thead>
                            <tr>
                              {["Component", "Required by Production", `Available in ${j.failure.site_id}-SFG`, "Short By"].map((h) => (
                                <th key={h} className="border border-[#FECDCA] px-2 py-1 text-left font-bold text-[#B42318]">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {j.failure.short_components.map((c) => (
                              <tr key={c.product_id} data-testid={`active-order-sfg-shortage-row-${i}-${c.product_id}`}>
                                <td className="border border-[#FECDCA] px-2 py-1">{c.product_id}{c.description ? ` - ${c.description}` : ""}</td>
                                <td className="border border-[#FECDCA] px-2 py-1 text-right tabular-nums">{formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}</td>
                                <td className="border border-[#FECDCA] px-2 py-1 text-right tabular-nums">{c.available_qty == null ? "unknown" : `${formatQty(c.available_qty)} ${formatUnit(c.unit_of_measure)}`}</td>
                                <td className="border border-[#FECDCA] px-2 py-1 text-right tabular-nums font-bold text-[#B42318]">{formatQty(Math.max(0, c.required_qty - (c.available_qty || 0)))} {formatUnit(c.unit_of_measure)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="text-[11px] text-[#93370D] mt-1.5">
                          Create/confirm a production order for these sub-assemblies first, then:
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={refreshingSfgStock}
                          onClick={() => refreshLiveSfgStock(j.failure.site_id)}
                          className="mt-1 h-7 text-[11px]"
                          data-testid={`active-order-sfg-refresh-button-${i}`}
                        >
                          <ArrowClockwise size={12} className={`mr-1 ${refreshingSfgStock ? "animate-spin" : ""}`} />
                          {refreshingSfgStock ? `Refreshing (${refreshSfgElapsed}s)...` : "Refresh Live SFG Stock & Retry"}
                        </Button>
                      </td>
                    </tr>
                  )}
                  {j.status === "paused_sap_block" && (
                    <tr data-testid={`active-order-sap-block-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FFFAEB]">
                        <p className="text-[11px] text-[#B54708] mb-1">
                          <strong>SAP says:</strong>{" "}
                          <span className="font-mono" data-testid={`active-order-sap-block-error-${i}`}>
                            {j.sapError || j.failure?.sap_block?.sap_error || "Release did not go through"}
                          </span>
                        </p>
                        <p className="text-[11px] text-[#93370D]" data-testid={`active-order-sap-block-message-${i}`}>{j.error}</p>
                        {j.failure?.sap_block?.deleted_proposal_id ? (
                          <p className="text-[11px] text-[#93370D] mt-1" data-testid={`active-order-dead-proposal-${i}`}>
                            Proposal <strong>{j.failure.sap_block.deleted_proposal_id}</strong>{" "}
                            {j.failure.sap_block.proposal_deleted
                              ? "was deleted in SAP - it was created against a source of supply SAP has since replaced, so it could never become an Order."
                              : "could NOT be deleted in SAP automatically - close it there yourself, it can never become an Order."}{" "}
                            Fix the Production Model in SAP, then create this order again (Retry cannot help here).
                          </p>
                        ) : (
                          <p className="text-[11px] text-[#93370D] mt-1">
                            Proposal <strong>{j.failure?.production_proposal_id || j.productionProposalId}</strong>
                            {j.failure?.production_order_id ? <> and Order <strong>{j.failure.production_order_id}</strong></> : null} already exist in SAP and were not consumed -
                            the app stopped retrying so nothing piles up. Fix it in SAP and click <strong>Retry</strong>
                            {j.failure?.production_order_id ? <>, or release order <strong>{j.failure.production_order_id}</strong> yourself in SAP</> : null}.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  {j.status === "failed" && j.failure && j.failure.reason !== "sfg_shortage" && (
                    <tr data-testid={`active-order-pipeline-error-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FEF3F2]">
                        <p className="text-[11px] text-[#B42318] mb-1" data-testid={`active-order-pipeline-error-reason-${i}`}>
                          <strong>Why this failed:</strong> {j.error || "SAP reported an unexpected error"}
                        </p>
                        {j.failure.production_proposal_id ? (
                          <p className="text-[11px] text-[#93370D]">
                            Proposal <strong>{j.failure.production_proposal_id}</strong>
                            {j.failure.production_order_id ? <> and Order <strong>{j.failure.production_order_id}</strong></> : null} already exist in SAP -
                            click <strong>Resume</strong> to let the app pick up from here automatically (no need to open the SAP UI).
                          </p>
                        ) : (
                          <p className="text-[11px] text-[#93370D]">
                            Nothing was created in SAP yet before this failed - fix the issue above and create a new order.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  {j.expanded && j.storeRequest && (
                    <tr data-testid={`active-order-details-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FFFAEB]">
                        <p className="text-[11px] text-[#93370D] mb-1.5">
                          Proposal <strong>{j.storeRequest.production_proposal_id}</strong> was created in SAP - {j.storeRequest.components.length} component(s) short at {j.storeRequest.site_id}.
                        </p>
                        <table className="w-full text-[11px] border-collapse bg-white">
                          <thead>
                            <tr>
                              {["Component", "Required by Production", `In Stock at Site ${j.storeRequest.site_id} (By Warehouse)`, ...(j.status === "partial_pending_planner" ? ["Issued", "Shortfall"] : [])].map((h) => (
                                <th key={h} className="border border-[#FEDF89] px-2 py-1 text-left font-bold text-[#93370D]">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {j.storeRequest.components.map((c) => (
                              <tr key={c.product_id}>
                                <td className="border border-[#FEDF89] px-2 py-1 align-top">{c.product_id}{c.description ? ` - ${c.description}` : ""}</td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums align-top">{formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}</td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums align-top">
                                  {(c.locations && c.locations.length > 0) ? c.locations.map((loc, li) => {
                                    const restricted = isLocationRestricted(loc);
                                    return (
                                      <div key={li} className={restricted ? "text-[#B54708] font-bold" : ""}>
                                        {loc.warehouse || (loc.site ? loc.site.split("-").pop() : "Unknown Warehouse")}{loc.stock_status ? ` (${loc.stock_status})` : ""}{restricted ? " \u26A0 On Hold" : ""}: {formatQty(loc.qty)} {formatUnit(c.unit_of_measure)}
                                      </div>
                                    );
                                  }) : "no stock at this site"}
                                </td>
                                {j.status === "partial_pending_planner" && (
                                  <>
                                    <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums align-top">{formatQty(c.issued_qty)} {formatUnit(c.unit_of_measure)}</td>
                                    <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums font-bold text-[#B42318] align-top">{formatQty(c.shortfall)} {formatUnit(c.unit_of_measure)}</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {j.status === "waiting_store_approval" && (
                          <p className="text-[11px] text-[#93370D] mt-1.5">
                            Share this Request ID with the store team on the Store Approval screen (<code>/storeapproval</code>): <strong data-testid={`store-approval-request-id-${i}`}>{j.storeRequest._id}</strong>
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {activeJobs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]" data-testid="active-orders-empty-state">No orders in progress - anything you create above will show up here.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto">
        <table className="w-full text-[12px] border-collapse" data-testid="proposal-history-table">
          <thead>
            <tr>
              {["When", "By", "Action", "Product/Order", "Production Model", "Site", "Qty", "Result"].map((h) => (
                <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map((h, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`proposal-history-row-${i}`}>
                <td className="border border-[#D0D5DD] px-2 py-1">{new Date(h.at).toLocaleString("en-IN")}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">{h.actor}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">
                  {h.type !== "proposal_created" ? "Order Released" : h.production_order_id ? "Proposal → Order" : "Proposal Created"}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1">
                  {h.type !== "proposal_created"
                    ? h.production_order_id
                    : h.production_order_id
                      ? `${h.material_id} (Proposal ${h.production_proposal_id} \u2192 Order ${h.production_order_id})`
                      : `${h.material_id} (Proposal ${h.production_proposal_id})`}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1 text-[#475467]" data-testid={`proposal-history-model-cell-${i}`}>{h.production_model_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">{h.site_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1 text-right tabular-nums">{h.quantity ?? "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">
                  {h.type !== "proposal_created"
                    ? h.success ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Released</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Failed</Badge>
                    : h.production_order_id
                      ? h.released ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Released</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Release Failed</Badge>
                      : <Badge variant="outline" className="bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]">Created</Badge>}
                </td>
              </tr>
            ))}
            {!loadingHistory && history.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]">No Production Orders created yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -------------------- Main page --------------------
const ScrapTrendCard = ({ scrapTrend }) => {
  const total = scrapTrend.breakdown.reduce((s, r) => s + (r.total_scrap || 0), 0);
  return (
    <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] flex flex-col gap-1.5" data-testid="stat-scrap-trend">
      <div className="flex items-center gap-1.5 text-[#475467]">
        <WarningCircle size={14} weight="bold" />
        <span className="font-heading text-xs font-bold uppercase tracking-wider">Scrap ({scrapTrend.days}d)</span>
      </div>
      <span className="font-sans text-2xl font-bold tabular-nums text-[#1D2939]">{formatQty(total)}</span>
      {scrapTrend.breakdown.length > 0 && (
        <div className="text-[11px] text-[#667085] space-y-0.5">
          {scrapTrend.breakdown.slice(0, 3).map((r) => (
            <div key={r.code || "none"} data-testid={`scrap-trend-reason-${r.code || "none"}`}>{r.label}: {formatQty(r.total_scrap)} ({r.count})</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProductionConfirmationTestPage() {
  // Aug 2026, user's explicit ask (same change as Store Approval): the
  // manual "Your name" box is gone - this page already requires Entra ID
  // login, so the acting person's name comes straight from their
  // signed-in session instead.
  const { user } = useAuth();
  const actorName = user?.name || user?.email || "";
  const [statusFilter, setStatusFilter] = useState("open");
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [sortLatestFirst, setSortLatestFirst] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [manualLotId, setManualLotId] = useState("");
  const [confirmRow, setConfirmRow] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [showReasons, setShowReasons] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [stockByRow, setStockByRow] = useState({});
  const [lastConfirmationByLot, setLastConfirmationByLot] = useState({});
  const [confirmedToday, setConfirmedToday] = useState(0);
  const [scrapTrend, setScrapTrend] = useState({ breakdown: [], days: 7 });

  useEffect(() => {
    axios.get(`${API}/production-confirmation/confirmed-today`).then(({ data }) => setConfirmedToday(data.confirmed_today)).catch(() => {});
    axios.get(`${API}/production-confirmation/scrap-trend`).then(({ data }) => setScrapTrend(data)).catch(() => {});
  }, []);

  const loadReasons = useCallback(() => {
    axios.get(`${API}/production-confirmation/deviation-reasons`).then(({ data }) => setReasons(data.reasons)).catch(() => {});
  }, []);

  useEffect(() => {
    if (rows.length === 0) {
      setStockByRow({});
      return;
    }
    axios.post(`${API}/production-confirmation/component-availability-batch`, {
      rows: rows.map((r) => ({ main_output_product: r.main_output_product, quantity: r.open_quantity || 0, site_id: r.site_id, material_inputs: r.material_inputs || null })),
      // See the Confirm dialog's own check: on this multi-operation page an
      // operation with no assigned input is reported as such, never guessed
      // from the output product's BOM.
      operation_scoped_inputs: true,
    }).then(({ data }) => {
      const map = {};
      rows.forEach((r, i) => { map[rowKey(r)] = data.results[i]; });
      setStockByRow(map);
    }).catch(() => {});
  }, [rows]);

  useEffect(() => {
    const lotIds = [...new Set(rows.map((r) => r.production_lot_id))];
    if (lotIds.length === 0) {
      setLastConfirmationByLot({});
      return;
    }
    axios.post(`${API}/production-confirmation/history/latest-batch`, { production_lot_ids: lotIds })
      .then(({ data }) => setLastConfirmationByLot(data))
      .catch(() => {});
  }, [rows]);

  const loadOpenLots = useCallback(async () => {
    setLoading(true);
    setAuthError(null);
    setLoadError(null);
    try {
      // Oct 3 2026, user's ask: show the Reporting Point DESCRIPTION, not
      // its code. `resolve_rp_descriptions` lets the backend work out the
      // Production Model (and from it the Bill of Operations' operation
      // names) for lots the app never created itself. This page only - the
      // main Production Confirmation page is unchanged.
      // `rp_markers_only` (Jun 2026): on THIS page the Reporting Point column
      // carries SAP's RP ID + the RP's own description and nothing else, so
      // the backend must not substitute the preceding Operation's name.
      const { data } = await axios.get(`${API}/production-confirmation/open-lots`, { params: { status: statusFilter, resolve_rp_descriptions: true, rp_markers_only: true } });
      setRows(data.rows);
    } catch (e) {
      if (e.response?.status === 403) setAuthError(e.response.data.detail);
      else setLoadError(e.response?.data?.detail || "Failed to load production lots from SAP");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadReasons(); }, [loadReasons]);
  useEffect(() => { loadOpenLots(); }, [loadOpenLots]);

  const lookupManual = async () => {
    if (!manualLotId.trim()) return;
    setLoading(true);
    setAuthError(null);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/lot/${manualLotId.trim()}`, { params: { resolve_rp_descriptions: true, rp_markers_only: true } });
      setRows(data.rows);
      toast.success(`Loaded Production Lot ${manualLotId.trim()}`);
    } catch (e) {
      if (e.response?.status === 403) setAuthError(e.response.data.detail);
      else toast.error(e.response?.data?.detail || "Production lot not found");
    } finally {
      setLoading(false);
    }
  };

  const onConfirmed = (row) => {
    setConfirmRow(null);
    setRows((prev) => prev.filter((r) => rowKey(r) !== rowKey(row)));
  };

  const [reopeningKey, setReopeningKey] = useState(null);
  const reopenTask = async (row) => {
    setReopeningKey(rowKey(row));
    try {
      await axios.post(`${API}/production-confirmation/restart-task`, {
        production_lot_id: row.production_lot_id,
        production_lot_uuid: row.production_lot_uuid,
        confirmation_group_uuid: row.confirmation_group_uuid,
        production_task_id: row.production_task_id,
        production_task_uuid: row.production_task_uuid,
        actor: actorName.trim() || "Unknown",
      });
      toast.success(`Reopened ${row.operation_description || row.reporting_point_id} - ready for more confirmations`);
      setRows((prev) => prev.map((r) => (rowKey(r) === rowKey(row) ? { ...r, task_finished: false } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reopen task in SAP");
    } finally {
      setReopeningKey(null);
    }
  };

  // Oct 7 2026, user's explicit ask: fire SAP's WIP Clearing Run with
  // TestRunIndicator=true, which computes the run and posts NOTHING. SAP's
  // response carries only a status + log and never reports journal entries,
  // so the amounts still have to be read from the Inventory Valuation work
  // centre - this just lets an admin fire the simulation for any lot.
  const [simulatingKey, setSimulatingKey] = useState(null);
  const simulateWip = async (row) => {
    setSimulatingKey(rowKey(row));
    try {
      const { data } = await axios.post(`${API}/production-confirmation/simulate-wip-clearing`, {
        production_lot_id: row.production_lot_id,
        site_id: row.site_id,
      });
      const through = data.sub_batch_through_every_operation || 0;
      if (!data.wip_clearing?.success) {
        toast.error(`SAP refused the simulated run: ${data.wip_clearing?.log || "no reason given"}`);
      } else if (data.all_operations_finished) {
        toast.success(`Simulation accepted - every operation on Lot ${row.production_lot_id} is finished. Check Inventory Valuation for the computed amounts.`);
      } else if (through > 0) {
        toast.success(`Simulation accepted - ${through} unit(s) have cleared every operation on Lot ${row.production_lot_id}. Check Inventory Valuation for the computed amounts (nothing was posted).`);
      } else {
        toast.info(`Simulation accepted but nothing has cleared every operation on Lot ${row.production_lot_id} yet - expect zero journal entries.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to simulate WIP Clearing");
    } finally {
      setSimulatingKey(null);
    }
  };

  // "Show mine" compares against the same actor string this app itself
  // writes on order creation (see createProposal's `actor: actorName.trim()`
  // below) - a case-insensitive match so a slightly different casing from
  // Entra ID doesn't silently hide a user's own orders.
  const visibleRows = useMemo(() => {
    let out = rows;
    if (creatorFilter === "mine") {
      const mine = actorName.trim().toLowerCase();
      out = out.filter((r) => (r.created_by || "").trim().toLowerCase() === mine);
    }
    if (sortLatestFirst) {
      out = [...out].sort((a, b) => {
        const at = a.order_created_at ? new Date(a.order_created_at).getTime() : -Infinity;
        const bt = b.order_created_at ? new Date(b.order_created_at).getTime() : -Infinity;
        return bt - at;
      });
    }
    return out;
  }, [rows, creatorFilter, sortLatestFirst, actorName]);

  // Aug 2026 - admin test feature: for a lot with multiple Reporting
  // Points (e.g. RP10 Blanking -> RP20 Bending -> END Forming), shows how
  // many units have cleared the PREVIOUS step but not yet this one - e.g.
  // "80 pcs awaiting Bending" when Blanking confirmed 100 and Bending has
  // only confirmed 20 so far. Rows for the same lot arrive from SAP
  // already in routing order, so a simple adjacent-pair diff within each
  // lot's own group is enough - no new material or BOM change needed.
  const awaitingByRowKey = useMemo(() => {
    const map = {};
    const byLot = {};
    rows.forEach((r) => {
      (byLot[r.production_lot_id] = byLot[r.production_lot_id] || []).push(r);
    });
    Object.values(byLot).forEach((group) => {
      if (group.length < 2) return;
      for (let i = 1; i < group.length; i++) {
        const prev = group[i - 1];
        const awaiting = (prev.total_confirmed_quantity || 0) - (group[i].total_confirmed_quantity || 0);
        map[rowKey(group[i])] = { awaiting, prevReportingPointId: prev.operation_description || prev.reporting_point_id, prevTotalConfirmed: prev.total_confirmed_quantity || 0 };
      }
    });
    return map;
  }, [rows]);


  return (
    <div className="print:hidden h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <Toaster position="top-right" />

      <header className="min-h-16 bg-[#7C2D12] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Production Confirmation - Multi-Op Admin Test</span>
          </div>
          <Badge variant="outline" className="bg-white/15 text-white border-white/30 ml-1" data-testid="admin-test-badge">ADMIN TEST</Badge>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto p-4 space-y-4">
        <Alert className="bg-[#FFFAEB] border-[#FEDF89]" data-testid="admin-test-page-banner">
          <Flask size={16} className="text-[#B54708]" weight="bold" />
          <AlertTitle className="text-[#B54708]">Admin Test Page - not visible to regular users</AlertTitle>
          <AlertDescription className="text-[#B54708] text-sm">
            Same Production Confirmation flow, plus two differences for testing multi-Reporting-Point models
            (e.g. PL-0037A_2's Blanking/Bending/Forming split): the "Mark as finished" checkbox is unlocked (so you can
            post a partial batch and leave a step open for later), and an "Awaiting Prev Step" column shows how many
            units cleared the previous operation but not this one yet.
            {" "}Leave "Mark as finished" <strong>OFF</strong> for a partial - SAP's Confirmation Finished indicator is
            permanent, and a Reporting Point closed with quantity still open can never be confirmed or reopened again.
          </AlertDescription>
        </Alert>
        <Tabs defaultValue="create" className="space-y-4">
          <TabsList data-testid="page-tabs">
            <TabsTrigger value="create" data-testid="tab-create-order">Create Production Order</TabsTrigger>
            <TabsTrigger value="confirm" data-testid="tab-confirm-production">Production Confirmation</TabsTrigger>
            <TabsTrigger value="my-requests" data-testid="tab-my-requests">My Stock Requests</TabsTrigger>
          </TabsList>

          <TabsContent value="create">
            <CreateOrderTab actorName={actorName} />
          </TabsContent>

          <TabsContent value="my-requests">
            <MyStockRequestsTab actorName={actorName} />
          </TabsContent>

          <TabsContent value="confirm" className="space-y-4">
        {authError && (
          <Alert className="bg-[#FFFAEB] border-[#FEDF89]" data-testid="auth-error-banner">
            <WarningCircle size={16} className="text-[#B54708]" />
            <AlertTitle className="text-[#B54708]">SAP Authorization Required</AlertTitle>
            <AlertDescription className="text-[#B54708] text-sm">
              {authError} Ask your SAP admin to go to <strong>Application and User Management → Communication Arrangements</strong>,
              activate the <strong>"Query Production Lots"</strong> and <strong>"Manage Production Lots"</strong> Communication Scenarios,
              and authorize the technical user <strong>_EMERGENTBOM</strong> for them.
            </AlertDescription>
          </Alert>
        )}
        {loadError && !authError && (
          <Alert className="bg-[#FEF3F2] border-[#FECDCA]" data-testid="load-error-banner">
            <WarningCircle size={16} className="text-[#B42318]" />
            <AlertDescription className="text-[#B42318] text-sm">{loadError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56 bg-white" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open (Released/Started)</SelectItem>
              <SelectItem value="all">All Statuses</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadOpenLots} data-testid="refresh-lots-button">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
          <div className="w-px h-6 bg-[#D0D5DD] mx-1" />
          {user?.role !== "user" && (
            <Select value={creatorFilter} onValueChange={setCreatorFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="creator-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="creator-filter-all">Show all</SelectItem>
                <SelectItem value="mine" data-testid="creator-filter-mine">Show mine</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            onClick={() => setSortLatestFirst((s) => !s)}
            className={sortLatestFirst ? "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" : ""}
            data-testid="sort-latest-toggle-button"
          >
            <ClockCounterClockwise size={14} className="mr-1.5" /> Sort by latest {sortLatestFirst ? "✓" : ""}
          </Button>
          <div className="w-px h-6 bg-[#D0D5DD] mx-1" />
          <Input
            placeholder="Enter Production Lot ID..."
            value={manualLotId}
            onChange={(e) => setManualLotId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookupManual()}
            className="w-56 bg-white"
            data-testid="manual-lot-id-input"
          />
          <Button variant="outline" onClick={lookupManual} data-testid="manual-lookup-button">
            <MagnifyingGlass size={14} className="mr-1.5" /> Look Up
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => setShowHistory(true)} data-testid="open-history-button">
            <ClockCounterClockwise size={14} className="mr-1.5" /> History
          </Button>
          <Button variant="outline" onClick={() => setShowReasons(true)} data-testid="open-manage-reasons-button">
            <Gear size={14} className="mr-1.5" /> Manage Reasons
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={ListChecks} label="Reporting Points Shown" value={visibleRows.length} testId="stat-reporting-points" />
          <StatCard icon={CheckCircle} label="Distinct Lots" value={new Set(visibleRows.map((r) => r.production_lot_id)).size} testId="stat-distinct-lots" />
          <StatCard icon={CheckCircle} label="Confirmed Today" value={confirmedToday} testId="stat-confirmed-today" />
          <ScrapTrendCard scrapTrend={scrapTrend} />
        </div>

        {/* Nov 3 2026 (same as the live page): skeletons only on the first
            load - a refresh keeps the rows usable instead of blanking the
            table for the 15-25s SAP takes to answer. */}
        {loading && rows.length === 0 ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-sm" />)}</div>
        ) : (
          <div className="border border-[#D0D5DD] rounded-sm overflow-auto bg-white">
            {loading && (
              <div className="bg-[#F9FAFB] border-b border-[#EAECF0] px-3 py-1.5 text-xs text-[#475467] flex items-center gap-1.5" data-testid="lots-refreshing-strip">
                <ArrowClockwise size={12} className="animate-spin" /> Refreshing from SAP - the list below stays usable until the new data arrives
              </div>
            )}
            <table className={`w-full text-[13px] border-collapse transition-opacity ${loading ? "opacity-60" : "opacity-100"}`} data-testid="production-lots-table">
              <thead>
                <tr>
                  {["Lot ID", "Output Product", "Site", "End Date", "Status", "Operation ID", "Reporting Point", "Task ID", "Planned", "Confirmed So Far", "Open", "Awaiting Prev Step", "UOM", "Finished", "Created By", "Production Model", "Stock", "Last Confirmation", ""].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => {
                  const stock = stockByRow[rowKey(r)];
                  const lastConf = lastConfirmationByLot[`${r.production_lot_id}::${r.reporting_point_id}`];
                  const awaiting = awaitingByRowKey[rowKey(r)];
                  // SAP permanently closed this Reporting Point for
                  // confirmation and will not reopen it - see
                  // sapConfirmationClosed in ConfirmDialog above.
                  const rowSapClosed =
                    r.confirmation_finished === true && (r.open_quantity || 0) > 0 && !r.task_finished;
                  // Jun 2026, user's explicit ask: this column carries ONLY
                  // SAP's Reporting Point ID and that Reporting Point's own
                  // description ("RP_10 - SHEARING"), in SAP's own order.
                  // The Operation ID has its own column now, and an
                  // operation's name is never shown in place of a Marker
                  // that genuinely has no description in SAP - such a row
                  // shows the bare RP ID (backend: rp_markers_only).
                  const rpLabel = r.reporting_point_description && r.reporting_point_id
                    ? `${r.reporting_point_id} - ${r.reporting_point_description}`
                    : (r.reporting_point_id || r.reporting_point_description || "—");
                  // Oct 2026, user's explicit rule: "WIP entry should be
                  // posted only on the END task confirm" - so everything WIP
                  // (the Simulate WIP action, the WIP chip) is shown on the
                  // END Reporting Point only.
                  const isEndRp = (r.reporting_point_id || "").trim().toUpperCase() === "END";
                  return (
                  <tr key={rowKey(r)} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`lot-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#101828]">{r.production_lot_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.main_output_product || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{r.site_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] whitespace-nowrap" data-testid={`end-date-cell-${i}`}>{formatEndDate(r.production_end_date)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Badge variant="outline" className={`${STATUS_TONE[r.life_cycle_status_label] || "bg-slate-100 text-slate-600 border-slate-200"} border`}>{r.life_cycle_status_label}</Badge>
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] font-mono whitespace-nowrap" data-testid={`operation-id-cell-${i}`}>{r.operation_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]" data-testid={`reporting-point-cell-${i}`}>
                      {/* Reporting Point ID + the Reporting Point's own
                          description, exactly as SAP's Bill of Operations
                          holds it - see rpLabel above. */}
                      {rpLabel}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] font-mono" data-testid={`task-id-cell-${i}`}>{r.production_task_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.planned_quantity)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.total_confirmed_quantity)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold text-[#B54708]">{formatQty(r.open_quantity)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums" data-testid={`awaiting-prev-step-${i}`}>
                      {!awaiting ? (
                        <span className="text-[#98A2B3]">—</span>
                      ) : (
                        <span className={awaiting.awaiting > 0 ? "font-bold text-[#B54708]" : "text-[#475467]"}>
                          {formatQty(awaiting.awaiting)} <span className="text-[10px] text-[#98A2B3]">(after {awaiting.prevReportingPointId})</span>
                        </span>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{formatUnit(r.unit_code) || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.task_finished ? "Yes" : "No"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]" data-testid={`created-by-cell-${i}`}>{r.created_by || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]" data-testid={`production-model-cell-${i}`}>{r.production_model_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {!stock ? (
                        <span className="text-[11px] text-[#98A2B3]" data-testid={`stock-badge-loading-${i}`}>…</span>
                      ) : stock.no_inputs_assigned ? (
                        <span className="text-[11px] text-[#98A2B3]" title="No input item is assigned to this operation in SAP - nothing to check" data-testid={`stock-badge-no-inputs-${i}`}>No input assigned</span>
                      ) : !stock.checked ? (
                        <span className="text-[11px] text-[#98A2B3]" data-testid={`stock-badge-unknown-${i}`}>No BOM cached</span>
                      ) : stock.sufficient_all ? (
                        <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6] border" data-testid={`stock-badge-ok-${i}`}>OK</Badge>
                      ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA] border cursor-pointer" data-testid={`stock-badge-short-${i}`}>
                              Short ({stock.short_components.length})
                            </Badge>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto max-w-sm bg-white border border-[#D0D5DD] text-[#1D2939] shadow-lg p-3 text-xs space-y-1" data-testid={`stock-tooltip-${i}`}>
                            <div className="font-bold font-heading uppercase tracking-wide text-[10px] text-[#667085] mb-1">Short Components</div>
                            {stock.short_components.map((c) => (
                              <div key={c.product_id}>
                                {c.product_id}: need {formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}, have {c.available_qty === null ? "no data" : `${formatQty(c.available_qty)} ${formatUnit(c.unit_of_measure)}`}
                              </div>
                            ))}
                          </PopoverContent>
                        </Popover>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`last-confirmation-cell-${i}`}>
                      <LastConfirmationBadges
                        data={lastConf}
                        productionLotId={r.production_lot_id}
                        lastConfKey={`${r.production_lot_id}::${r.reporting_point_id}`}
                        isEndReportingPoint={isEndRp}
                        taskFinished={r.task_finished}
                        openQuantity={r.open_quantity}
                        byproductOutput={(r.material_outputs || []).find((mo) => mo.product_id !== r.main_output_product)}
                        siteId={r.site_id}
                        mainOutputProduct={r.main_output_product}
                        unitCode={r.unit_code}
                        actorName={actorName}
                        onRetried={(lotId, patch) => setLastConfirmationByLot((prev) => (
                          prev[lotId] ? { ...prev, [lotId]: { ...prev[lotId], ...patch } } : prev
                        ))}
                      />
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {r.locked ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1.5 py-0.5" data-testid={`lock-badge-${i}`} title={r.lock_reason}>
                          <LockSimple size={11} weight="bold" /> {r.lock_reason}
                        </span>
                      ) : (
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          disabled={loading || r.task_finished || rowSapClosed || (awaiting && awaiting.awaiting <= 0)}
                          onClick={() => setConfirmRow(r)}
                          title={
                            r.task_finished ? "Task genuinely Finished in SAP - re-confirmation disabled to avoid a duplicate posting"
                            : rowSapClosed ? "SAP permanently closed this Reporting Point for confirmation (Confirmation Finished indicator set) and refuses to reopen it - needs a correction in SAP"
                            : (awaiting && awaiting.awaiting <= 0) ? `Nothing has cleared ${awaiting.prevReportingPointId || "the previous step"} yet - confirm that step first`
                            : undefined
                          }
                          data-testid={`confirm-button-${i}`}
                        >
                          Confirm
                        </Button>
                        {rowSapClosed && (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm px-1.5 py-0.5"
                            title="An earlier confirmation was posted with Finished ticked. SAP's Confirmation Finished indicator is permanent and it will not reopen the task while its status is In Process."
                            data-testid={`sap-closed-badge-${i}`}
                          >
                            <WarningCircle size={11} weight="bold" /> Closed in SAP
                          </span>
                        )}
                        {r.task_finished && (r.total_confirmed_quantity || 0) < (r.planned_quantity || 0) && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={reopeningKey === rowKey(r)}
                            onClick={() => reopenTask(r)}
                            title="This task finished early - reopen it in SAP to post the remaining quantity"
                            data-testid={`reopen-task-button-${i}`}
                          >
                            {reopeningKey === rowKey(r) ? <CircleNotch size={14} className="animate-spin" /> : "Reopen Task"}
                          </Button>
                        )}
                        {isEndRp && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={simulatingKey === rowKey(r) || !r.site_id}
                            onClick={() => simulateWip(r)}
                            title="Fire SAP's WIP Clearing Run with TestRunIndicator=true - computes the run and posts nothing. Read the amounts in SAP's Inventory Valuation work centre."
                            data-testid={`simulate-wip-button-${i}`}
                          >
                            {simulatingKey === rowKey(r) ? <CircleNotch size={14} className="animate-spin" /> : "Simulate WIP"}
                          </Button>
                        )}
                      </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {rows.length === 0 && authError && (
                  <tr><td colSpan={19} className="text-center py-8 text-[#B54708] bg-[#FFFAEB] border border-[#D0D5DD]" data-testid="blocked-state">Blocked by SAP authorization - see banner above.</td></tr>
                )}
                {rows.length === 0 && !authError && !loadError && (
                  <tr><td colSpan={19} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="empty-state">No open production lots found.</td></tr>
                )}
                {rows.length > 0 && visibleRows.length === 0 && (
                  <tr><td colSpan={19} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="filtered-empty-state">No rows match "Show mine" - no open lots were created by you.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </main>

      <ConfirmDialog
        row={confirmRow}
        siblingRows={confirmRow ? rows.filter((r) => r.production_lot_id === confirmRow.production_lot_id) : []}
        actorName={actorName}
        onClose={() => setConfirmRow(null)}
        onConfirmed={onConfirmed}
        reasons={reasons}
        maxFromPrevStep={confirmRow ? awaitingByRowKey[rowKey(confirmRow)]?.prevTotalConfirmed : null}
        prevStepLabel={confirmRow ? awaitingByRowKey[rowKey(confirmRow)]?.prevReportingPointId : null}
      />
      <ManageReasonsDialog open={showReasons} onClose={() => setShowReasons(false)} reasons={reasons} onChanged={setReasons} />
      <HistoryDialog open={showHistory} onClose={() => setShowHistory(false)} />
    </div>
  );
}
